import { createIcons, Save, Eye, CheckCircle, FileDown, Plus, Trash2, Upload, PenLine, Send, ClipboardCheck, MessageSquareWarning } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { aiReviewSlot, bindAiReviewHost } from "../components/ai-review-host.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import { AppError } from "../../shared/errors/app-error.js";
import { groupMePendencies, isMeClosed, isMeHighTemperature, meEffectiveThickness, meWorkflowStatusLabel, METHOD_A_LABEL, recomputeMeThermalCorrection, type MeReport, type MeReportItem, type MeResultPoint, type MeSignatory } from "../../domain/engineering/me-report-domain.js";
import {
  ME_HIGH_TEMPERATURE_LEGEND,
  ME_HIGH_TEMPERATURE_THRESHOLD_C,
  ME_TEMPERATURE_MAX_C,
  formatFactor,
  materialFamilyLabel
} from "../../domain/engineering/me-thermal-correction.js";
import { automaticInspectorId, canAssignInspector, resolveMeSubOs } from "../../domain/engineering/me-inspector-subos.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker, loadList } from "../context/context-load.js";
import { CONTEXT_HYDRATION_STYLES, hydrateDocumentContext, hydrationBanner, type HydrationReport } from "../context/context-hydration.js";
import "../components/searchable-select.js";
import {
  ME_LAUDO_LABELS,
  ME_LAUDO_SYMBOLS,
  allowedLaudoOverrides,
  formatDecimalBR,
  isLaudoOverrideAllowed,
  parseDecimalBR,
  resolveFinalLaudo,
  resolveLaudo,
  suggestNextPointId
} from "../../domain/engineering/me-measurements.js";
import {
  ME_MEASUREMENT_TECHNIQUES,
  formatProcedureLabel,
  isOtherTechnique,
  type NdtProcedure
} from "../../domain/ndt/ndt-procedure.js";
import {
  COUPLANT_OTHER,
  ME_SURFACE_CONDITIONS,
  formatCatalogItemCode,
  formatCatalogItemLabel,
  type NdtCatalogItem
} from "../../domain/ndt/ndt-technical-catalog.js";
import { formatCalibrationBlockRange, resolveCalibrationStatus } from "../../domain/ndt/ndt-calibration.js";
import { formatTransducerDimension, formatTransducerFrequency } from "../../domain/ndt/ndt-transducer.js";
import {
  buildMeSeedFromContext,
  filterEquipmentsByContext,
  missingMeContextFields,
  staleMeContextFields,
  type MeContextSeed
} from "./me-report-context.js";
import "../components/document/pdf-preview.js";
import { PLH_REVIEW_UI_STYLES, openPlhSubmitModal, plhApprovalPanel, plhApproveConfirmModal, plhSubmitButton, reviewBanner, reviewEditingLocked } from "../components/plh-review-ui.js";
import { HOLD_UI_STYLES } from "../components/hold-ui.js";
import { CORRECTION_UI_STYLES } from "../components/correction-request-ui.js";
import { bindNdtEngineeringEvents, canResolveCorrections, loadNdtEngineeringState, ndtDeepLinkFocus, ndtEngineeringBanners, ndtHoldButton, ndtHoldLocked, type NdtEngineeringState } from "./ndt-engineering-integration.js";
import type { TechnicalDocumentReview } from "../../domain/review/technical-document-review.js";
import { autosaveDelayMs, loadOperationalSettings } from "../settings/operational-settings.js";

const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
  PRESSURE_VESSEL: "Vaso de Pressão (VP)",
  STORAGE_TANK: "Tanque de Armazenamento (TQ)",
  HEAT_EXCHANGER: "Permutador de Calor (TC)",
  BOILER: "Caldeira (CD)",
  FURNACE: "Forno Industrial (FN)",
  PIPING_SYSTEM: "Sistema de Tubulação (ST)",
  PIPING_LINE: "Tubulação",
  OTHER: "Outro Equipamento"
};

const LAUDO_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "A", label: "A — Aprovado" },
  { value: "R", label: "R — Reprovado" },
  { value: "EC", label: "EC — Exame Complementar" },
  { value: "P", label: "P — Em Elaboração" },
  { value: "ADPC", label: "ADPC — A Definir p/ Cliente" }
];

class AppMeReportPage extends HTMLElement {
  private report: MeReport | null = null;
  private isLoading = true;
  private loadError: string | null = null;
  private isSaving = false;
  private saveNotice: string | null = null;
  /** O último autosave falhou? Governa a limpeza do aviso no sucesso seguinte. */
  private autosaveFailed = false;
  private validationErrors: string[] | null = null;
  private validationOk = false;

  private equipments: any[] = [];
  private professionals: any[] = [];
  /** ME procedures selectable for this report: active ones plus the one already linked (even if inactivated since). */
  private procedures: NdtProcedure[] = [];
  /** Technical catalogs and END assets feeding the section 02/03 selectors — never hardcoded in this page. */
  private norms: NdtCatalogItem[] = [];
  private criteria: NdtCatalogItem[] = [];
  private couplants: NdtCatalogItem[] = [];
  /** True while the couplant "OUTRO" free-text field should stay visible even before anything is typed. */
  private couplantOtherOpen = false;
  private calibrationBlocks: any[] = [];
  private devices: any[] = [];
  private transducers: any[] = [];

  // "CONTEXTO DO RELATÓRIO" — the documentary context is chosen here, on the
  // page itself. The session's global active client is only a pre-selection
  // convenience, never a requirement to open or fill an ME report.
  private clients: any[] = [];
  private projects: any[] = [];
  private unitAreas: any[] = [];
  private inspections: any[] = [];
  private workOrders: any[] = [];
  private subWorkOrders: any[] = [];
  private selection: {
    clientId: string;
    projectId: string;
    unitAreaId: string;
    equipmentId: string;
    inspectionId: string;
    workOrderId: string;
    subWorkOrderId: string;
  } = { clientId: "", projectId: "", unitAreaId: "", equipmentId: "", inspectionId: "", workOrderId: "", subWorkOrderId: "" };
  private existingReport: MeReport | null = null;
  /** Desfecho da reposição do contexto gravado — avisa o que não voltou. */
  private hydration: HydrationReport | null = null;
  private contextError: string | null = null;
  private isResolvingContext = false;

  private previewSnapshot: any | null = null;
  private previewOpen = false;

  private pointSeq = 0;
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * O RESULTADO de cada leitura da cascata, não só os itens.
   *
   * Antes cada `catch(() => [])` transformava um 403 em lista vazia, e o
   * select anunciava "Nenhuma OS cadastrada para este contexto" com a OS
   * cadastrada — mandando o usuário procurar um cadastro que já existia.
   * Guardar o status permite dizer a verdade: não existe, não pude ler, ou
   * falhou.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private boundFormInput = this.handleFormInput.bind(this);

  private boundFormBlur = this.handleFormBlur.bind(this);

  private boundSearchableChange = this.handleSearchableChange.bind(this) as EventListener;

  connectedCallback() {
    // INSPECT IA REVIEW: a revisão aplicou texto ⇒ relê o relatório do serviço.
    bindAiReviewHost(this, async () => {
      if (!this.report) return;
      const fresh = await window.app.platform.meReportService.getReport(this.scopedCtx(this.report.clientId), this.report.id).catch(() => null);
      if (fresh) { this.report = fresh; this.render(); }
    });
    this.addEventListener("input", this.boundFormInput);
    this.addEventListener("change", this.boundFormInput);
    // Dimensional fields are rewritten as 0,00 when the user leaves them —
    // "focusout" (not "blur") because it bubbles to this delegated listener.
    this.addEventListener("focusout", this.boundFormBlur);
    // O combobox pesquisável só emite depois de o valor ter sido confirmado
    // contra o catálogo, então aqui nunca chega texto digitado solto.
    this.addEventListener("searchable-change", this.boundSearchableChange);
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener("input", this.boundFormInput);
    this.removeEventListener("change", this.boundFormInput);
    this.removeEventListener("focusout", this.boundFormBlur);
    this.removeEventListener("searchable-change", this.boundSearchableChange);
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
  }

  /** Normalizes a dimensional field to 0,00 as soon as it loses focus. */
  private handleFormBlur(e: Event) {
    if (!this.report) return;
    const target = e.target as HTMLInputElement;
    if (!target || !target.dataset) return;

    const pointKey = target.dataset["pointId"];
    const field = target.dataset["pointField"];
    if (!pointKey || !field) return;
    if (field !== "nominalThickness" && field !== "minThickness" && field !== "actualThickness") return;

    const p = this.report.points.find(pt => pt.key === pointKey);
    if (!p) return;
    target.value = formatDecimalBR((p as any)[field]);
  }

  /** Ciclo de revisão PLH do relatório (mecanismo central) — decide botão, banner e bloqueio. */
  private review: TechnicalDocumentReview | null = null;

  /** LIBERAR PARA O PLH: confirmação simples e pendências dos itens 1 a 4. */
  private showReleaseConfirm = false;
  private releasing = false;
  private releasePendencies: Array<{ item: MeReportItem; label: string; messages: string[] }> | null = null;

  /** APROVAR RELATÓRIO (PLH): confirmação e envio. */
  private showApproveConfirm = false;
  private approving = false;
  private approveFailure: string | null = null;

  /** Painel de Engenharia: HOLD ativo e correções por item do relatório. */
  private engineering: NdtEngineeringState = { hold: null, corrections: [] };

  private async loadReview() {
    if (!this.report) return;
    try {
      this.review = await window.app.platform.technicalReviewService.getDocumentReview(this.scopedCtx(this.report.clientId), "ME", this.report.id);
    } catch { this.review = null; }
    this.engineering = await loadNdtEngineeringState("ME", this.report);
  }

  private holdSummary() {
    const r: any = this.report;
    return { documentNumber: r.reportNumber, revision: r.revision, clientId: String(r.clientId), client: r.clientDisplayName || "", project: r.projectContract || "", unitArea: r.unitArea || "", tag: r.equipmentTag || "", osNumber: r.osNumber || "", subOsNumber: r.subOsNumber || "", inspectorProfessionalId: r.inspectorProfessionalId || null, inspectorName: r.inspectorName || r.createdBy || "", route: `/ndt/thickness/${r.id}` };
  }

  /**
   * LIBERAR PARA O PLH.
   *
   * Os ÚNICOS impeditivos são dados obrigatórios incompletos nos ITENS 1 a 4.
   * Nada mais bloqueia: nem correção pendente, nem AVR, RI, Book, Minuta ou
   * END. E este fluxo NUNCA abre a janela de CORREÇÃO — correção só existe
   * quando o PLH devolve o documento.
   */
  private async onSubmitToPlh() {
    const r = this.report;
    if (!r) return;
    this.releasePendencies = null;
    this.showReleaseConfirm = false;
    try {
      const validation = await window.app.platform.meReportService.validateForPlhRelease(this.scopedCtx(r.clientId), r.id);
      if (!validation.valid) {
        this.releasePendencies = groupMePendencies(validation.pendencies);
        this.render();
        return;
      }
      this.showReleaseConfirm = true;
      this.render();
    } catch (e) {
      this.saveNotice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível validar o relatório.";
      this.render();
    }
  }

  /** CONFIRMAR E LIBERAR — envia pelo serviço central (gate do PLH, auditoria e idempotência). */
  private async confirmReleaseToPlh() {
    const r = this.report;
    if (!r || this.releasing) return;
    this.releasing = true;
    this.render();
    try {
      const review = await window.app.platform.technicalReviewService.submit(this.scopedCtx(r.clientId), "ME", r.id, {});
      this.review = review;
      this.report = (await window.app.platform.meReportService.getReport(this.scopedCtx(r.clientId), r.id)) || this.report;
      this.showReleaseConfirm = false;
      this.saveNotice = `RELATÓRIO ENVIADO AO PLH — o Relatório de Medição de Espessura foi liberado para revisão e aprovação do PLH ${review.assignedPlhName}. Status: AGUARDANDO REVISÃO DO PLH. Disponível no Painel de Engenharia → REVISÃO.`;
    } catch (e) {
      this.saveNotice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Erro ao liberar o relatório para o PLH.";
      this.showReleaseConfirm = false;
    }
    this.releasing = false;
    this.render();
  }

  /**
   * APROVAR RELATÓRIO — ato do PLH responsável pelo Cliente.
   *
   * APROVAR = FINALIZAR: uma só confirmação leva de EM APROVAÇÃO DO PLH a
   * FINALIZADO. O serviço central congela o snapshot da revisão, fecha o ME
   * no módulo (`finalizeOnPlhApproval`), grava a auditoria e bloqueia a
   * edição. Nada disso é decidido aqui — a tela só confirma e recarrega.
   */
  /**
   * INICIAR ANÁLISE — o primeiro passo do workflow do PLH.
   *
   * Faltava em tela: `TechnicalReviewService.startReview` existia, mas só a
   * tela de análise da Engenharia o chamava. No próprio relatório o PLH ia de
   * AGUARDANDO direto para aprovar, e o estado EM ANÁLISE nunca era
   * registrado — o painel não conseguia dizer que alguém já estava olhando o
   * documento, nem desde quando.
   *
   * Usa o MESMO serviço central dos demais tipos documentais; não há caminho
   * próprio do ME.
   */
  private async onStartPlhAnalysis() {
    const r = this.report;
    if (!r || !this.review) return;
    try {
      this.review = await window.app.platform.technicalReviewService.startReview(this.scopedCtx(r.clientId), this.review.id);
      this.saveNotice = `ANÁLISE INICIADA — o relatório está EM ANÁLISE DO PLH desde ${new Date(this.review.reviewStartedAt || Date.now()).toLocaleString("pt-BR")}.`;
    } catch (e) {
      // O motivo vem do servidor e é mostrado como está: recusar sem explicar
      // é o que fazia o usuário concluir que o sistema estava quebrado.
      this.saveNotice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível iniciar a análise.";
    }
    this.render();
  }

  private async confirmApproveReport() {
    const r = this.report;
    if (!r || !this.review || this.approving) return;
    this.approving = true;
    this.approveFailure = null;
    this.render();
    try {
      const review = await window.app.platform.technicalReviewService.approve(this.scopedCtx(r.clientId), this.review.id, {});
      this.review = review;
      this.report = (await window.app.platform.meReportService.getReport(this.scopedCtx(r.clientId), r.id)) || this.report;
      this.showApproveConfirm = false;
      this.saveNotice = `RELATÓRIO APROVADO E FINALIZADO — aprovado pelo PLH ${review.approvedBy?.name || ""} em ${new Date(review.approvedAt || Date.now()).toLocaleString("pt-BR")} (Rev. ${review.revision}). A revisão e os signatários estão congelados e a edição está bloqueada.`;
    } catch (e) {
      this.approveFailure = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível aprovar o relatório.";
    }
    this.approving = false;
    this.render();
  }

  /**
   * DEVOLVER PARA CORREÇÃO — leva à tela de análise.
   *
   * A devolução do PLH é POR ITEM (seção + item + observação) e o servidor
   * recusa um motivo genérico sem itens. Em vez de reproduzir aqui um
   * formulário mais fraco, o PLH vai ao lugar onde esse formulário já existe.
   */
  private returnForCorrection() {
    if (!this.review) return;
    router.navigate(`/engineering/review/${this.review.id}`);
  }

  /**
   * LIBERAR PARA O PLH — mesmo gate de permissão/ciclo do mecanismo central
   * (`plhSubmitButton`), com o rótulo desta etapa do ME. Depois de uma
   * devolução do PLH o rótulo vira REENVIAR, que é outro fluxo.
   */
  private renderReleaseButton(r: MeReport): string {
    const shared = plhSubmitButton(this.review, r.status, "btn-action");
    if (!shared) return "";
    const label = this.review?.status === "CORRECTION_REQUESTED" ? "REENVIAR PARA O PLH" : "LIBERAR PARA O PLH";
    return shared.replace(/<\/i>[^<]*<\/button>/, `</i> ${label}</button>`);
  }

  /** Confirmação simples (item 10) — sem nenhum campo de correção. */
  private renderReleaseConfirmModal(): string {
    if (!this.showReleaseConfirm) return "";
    return `
      <div class="me-modal-backdrop">
        <div class="me-modal">
          <div class="me-modal-head">LIBERAR RELATÓRIO PARA O PLH?</div>
          <div class="me-modal-body">
            <p>O Relatório de Medição de Espessura será enviado para revisão e aprovação do PLH.</p>
            <p>Após o envio, o documento ficará disponível no:<br><strong>PAINEL DE ENGENHARIA → REVISÃO</strong>.</p>
          </div>
          <div class="me-modal-actions">
            <button class="btn-action" id="btn-release-cancel" ${this.releasing ? "disabled" : ""}>CANCELAR</button>
            <button class="btn-action primary" id="btn-release-confirm" ${this.releasing ? "disabled" : ""}>${this.releasing ? "LIBERANDO..." : "CONFIRMAR E LIBERAR"}</button>
          </div>
        </div>
      </div>`;
  }

  /** Bloqueio por item incompleto (item 15) — lista só os itens com pendência. */
  private renderReleaseBlockedPanel(): string {
    const groups = this.releasePendencies;
    if (!groups || groups.length === 0) return "";
    return `
      <div class="me-modal-backdrop">
        <div class="me-modal">
          <div class="me-modal-head danger">NÃO É POSSÍVEL LIBERAR PARA O PLH</div>
          <div class="me-modal-body">
            <p>Existem informações obrigatórias incompletas no Relatório de Medição de Espessura.</p>
            <div class="me-pend-list">
              ${groups.map(g => `
                <div class="me-pend-group">
                  <button type="button" class="me-pend-item" data-goto-item="${g.item}">${this.escapeHtml(g.label)}</button>
                  <ul>${g.messages.map(m => `<li>${this.escapeHtml(m)}</li>`).join("")}</ul>
                </div>`).join("")}
            </div>
            <p class="me-pend-hint">Preencha os campos indicados antes de liberar o relatório.</p>
          </div>
          <div class="me-modal-actions">
            <button class="btn-action primary" id="btn-release-back">VOLTAR AO RELATÓRIO</button>
          </div>
        </div>
      </div>`;
  }

  private buildCtx(): any {
    const c = contextStore.get();
    return {
      activeClientId: c.activeClientId || undefined,
      permissions: c.permissions,
      correlationId: "",
      userId: c.userId,
      professionalId: c.professionalId || null
    };
  }

  /** Context scoped to the client picked on this page, so every cadastro query filters by it without depending on the global session client. */
  private scopedCtx(clientId?: string): any {
    const base = this.buildCtx();
    const target = clientId ?? this.selection.clientId;
    return target ? { ...base, activeClientId: target } : base;
  }

  private async init() {
    this.isLoading = true;
    this.render();
    await loadOperationalSettings();

    const ctx = this.buildCtx();
    const id = this.getAttribute("id");

    try {
      const professionals = await this.ctxLoad.load("Profissionais", () => window.app.platform.adminService.listProfessionals(ctx));
      this.professionals = professionals || [];

      if (id) {
        const existing = await window.app.platform.meReportService.getReport(ctx, id);
        if (!existing) {
          this.loadError = "Relatório ME não encontrado.";
          this.isLoading = false;
          this.render();
          return;
        }
        this.report = existing;
        await this.loadReview();
        // Equipment is per-client — reload it scoped to the client this
        // report actually belongs to, not to the session's.
        this.equipments = await window.app.platform.equipmentService
          .listEquipments(this.scopedCtx(existing.clientId), { clientId: existing.clientId })
          .catch(() => []);
        await this.loadProcedures();
        await this.loadTechnicalSources();
        await this.hydrateContextFromReport();
        this.pointSeq = this.computePointSeq(this.report.points);
        this.isLoading = false;
        this.render();
        return;
      }

      // No report id in the route: the page still opens — it shows the
      // "CONTEXTO DO RELATÓRIO" selector so the user picks
      // Cliente → Projeto → Unidade/Área → Equipamento → Inspeção here.
      await this.loadClients();
      this.isLoading = false;
      this.render();
    } catch (e: any) {
      console.error("[ME REPORT] Falha ao carregar página", e);
      this.loadError = e instanceof AppError ? ((e as any).safeMessage || e.message) : (e?.message || "Erro ao carregar o relatório.");
      this.isLoading = false;
      this.render();
    }
  }

  /**
   * Loads the ME procedures this report may offer. Inactive procedures are
   * excluded, except the one this report is already bound to — reopening an
   * older report must never lose its procedure (regra 7 do bloco).
   */
  private async loadProcedures() {
    try {
      this.procedures = await window.app.platform.ndtProcedureService
        .listSelectableForReport(this.buildCtx(), "ME", this.report?.procedureId ?? null);
    } catch (e) {
      console.error("[ME REPORT] Falha ao carregar procedimentos de END", e);
      this.procedures = [];
    }
  }

  /**
   * Loads the technical catalogs (normas, critérios, acoplantes) and the END
   * assets (bloco de calibração, aparelho de ME-US, transdutor) that feed the
   * selectors of sections 02 and 03. Inactive records are excluded, except
   * one this report already points to.
   */
  /**
   * HIDRATAÇÃO DO CONTEXTO DE UM RELATÓRIO JÁ GRAVADO.
   *
   * Antes daqui só o Cliente e a OS voltavam: Projeto, Unidade/Área, Ativo e
   * Inspeção ficavam fora da seleção e suas listas nem eram carregadas — os
   * seletores abriam vazios apesar de o documento carregar os IDs, e o
   * primeiro toque em qualquer campo disparava a cascata, que apaga tudo
   * abaixo. O contexto gravado era perdido por reabrir a tela.
   *
   * A hidratação NÃO é a cascata: nada aqui limpa o nível de baixo, porque
   * nada foi trocado pelo usuário. Cada nível só perde o valor quando ele
   * próprio sumiu da sua própria lista — e, nesse caso, a tela diz qual.
   */
  private async hydrateContextFromReport() {
    const report = this.report;
    if (!report) return;
    const ctx = () => this.buildCtx();
    const scoped = () => this.scopedCtx(report.clientId);

    this.hydration = await hydrateDocumentContext([
      {
        label: "o Cliente",
        persistedId: report.clientId,
        load: () => loadList<any>("os Clientes", () => window.app.platform.adminService.listAuthorizedClients(ctx())),
        apply: (value, options) => { this.selection.clientId = value || report.clientId; this.clients = [...options]; }
      },
      {
        label: "o Projeto",
        persistedId: report.projectId,
        load: () => loadList<any>("os Projetos", () => window.app.platform.projectService?.listProjects(this.selection.clientId) ?? Promise.resolve([])),
        apply: (value, options) => { this.selection.projectId = value; this.projects = [...options]; }
      },
      {
        label: "a Unidade / Área",
        persistedId: report.unitAreaId,
        load: () => loadList<any>("as Unidades / Áreas", () => window.app.platform.projectService?.listUnitAreas({ projectId: this.selection.projectId, clientId: this.selection.clientId }) ?? Promise.resolve([])),
        apply: (value, options) => { this.selection.unitAreaId = value; this.unitAreas = [...options]; }
      },
      {
        label: "a Ordem de Serviço",
        persistedId: report.osWorkOrderId,
        load: () => loadList<any>("as Ordens de Serviço", () => window.app.platform.adminService.listWorkOrdersForDocument(ctx(), report.clientId, this.selection.projectId || undefined)),
        apply: (value, options) => { this.selection.workOrderId = value; this.workOrders = [...options]; }
      },
      {
        label: "a Sub-OS",
        persistedId: report.osSubWorkOrderId,
        load: () => loadList<any>("as Sub-OS", () => this.selection.workOrderId
          ? window.app.platform.adminService.listSubWorkOrdersForDocument(ctx(), this.selection.workOrderId)
          : Promise.resolve([])),
        apply: (value, options) => { this.selection.subWorkOrderId = value; this.subWorkOrders = [...options]; }
      },
      {
        label: "o Equipamento / TAG",
        persistedId: report.equipmentId,
        // O Equipamento é lido no Cliente DO RELATÓRIO, não no da sessão.
        load: () => loadList<any>("os Equipamentos", () => window.app.platform.equipmentService.listEquipments(scoped(), { clientId: report.clientId })),
        apply: (value, options) => { this.selection.equipmentId = value; this.equipments = [...options]; }
      },
      {
        label: "a Inspeção",
        persistedId: report.inspectionId,
        // Vínculo OPCIONAL (CLAUDE.md §41): ausência não vira advertência.
        optional: true,
        load: () => loadList<any>("as Inspeções", () => this.selection.equipmentId
          ? window.app.platform.inspectionService.listInspections(scoped(), this.selection.equipmentId as any)
          : Promise.resolve([])),
        apply: (value, options) => { this.selection.inspectionId = value; this.inspections = [...options]; }
      }
    ]);

    // Depois de as Sub-OS existirem: resolve o Inspetor de quem não pode
    // designar outro e reaplica a cascata. É aqui e não no render porque
    // renderizar não pode gravar.
    this.applyAutomaticInspector();
    this.applySubOsForInspector();
  }

  /**
   * Recalcula a correção MÉTODO A no navegador enquanto a pessoa edita —
   * nunca num relatório fechado ou travado (lá vale o retrato aprovado). Vsa
   * vem do Procedimento já carregado em `this.procedures` (`loadProcedures`);
   * k e a Temperatura de Referência são as constantes fixas do Método A. A
   * gravação refaz o cálculo no servidor, que é a versão que vale.
   */
  private recomputeHot(): void {
    const r = this.report;
    if (!r || isMeClosed(r.status) || reviewEditingLocked(this.review) || ndtHoldLocked(this.engineering)) return;
    const procedure = r.procedureId ? this.procedures.find(p => p.id === r.procedureId) ?? null : null;
    recomputeMeThermalCorrection(r, {
      now: new Date().toISOString(),
      userId: contextStore.get().userId ? String(contextStore.get().userId) : null,
      procedure
    });
  }

  private async loadTechnicalSources() {
    const r = this.report;
    const ctx = this.buildCtx();
    // END assets are a GLOBAL technical catalog — the same tool serves every
    // client, so this lookup is deliberately not scoped to the report's client.
    const assetCtx = ctx;
    const catalog = window.app.platform.ndtCatalogService;
    const assets = window.app.platform.ndtAssetService;

    const [norms, criteria, couplants] = await Promise.all([
      catalog.listSelectableForReport(ctx, "REFERENCE_STANDARD", "ME", r?.referenceNormId ?? null).catch(() => []),
      catalog.listSelectableForReport(ctx, "ACCEPTANCE_CRITERIA", "ME", r?.acceptanceCriteriaId ?? null).catch(() => []),
      catalog.listSelectableForReport(ctx, "COUPLANT", "ME", r?.couplantId ?? null).catch(() => [])
    ]);
    this.norms = norms;
    this.criteria = criteria;
    this.couplants = couplants;

    // Only an INACTIVE asset is withheld. An expired calibration keeps the
    // equipment selectable (the report warns about it before validating) —
    // hiding it would leave the inspector unable to report what was used.
    const keepLinked = (list: any[], linkedId: string | null | undefined) =>
      list.filter(a => a.status !== "INACTIVE" || (!!linkedId && a.id === linkedId));

    const [blocks, devices, transducers] = await Promise.all([
      assets.listAssets(assetCtx, "CALIBRATION_BLOCK", true).catch(() => []),
      assets.listAssets(assetCtx, "ULTRASONIC_THICKNESS_GAUGE", true).catch(() => []),
      assets.listAssets(assetCtx, "ULTRASONIC_TRANSDUCER", true).catch(() => [])
    ]);
    this.calibrationBlocks = keepLinked(blocks, r?.calibrationBlockAssetId);
    this.devices = keepLinked(devices, r?.deviceAssetId);
    this.transducers = keepLinked(transducers, r?.transducerAssetId);
  }

  /** Latest calibration certificate of an END asset, or null when it has none registered. */
  private async latestCertificateOf(assetId: string): Promise<any | null> {
    try {
      const certs = await window.app.platform.ndtAssetService
        .listCertificates(this.scopedCtx(this.report?.clientId), assetId);
      return certs && certs.length > 0 ? certs[0] : null;
    } catch {
      return null;
    }
  }

  private onCatalogSelection(field: "referenceNorm" | "acceptanceCriteria" | "couplant", itemId: string) {
    if (!this.report) return;

    // "OUTRO" is not a catalog record: it clears the link and opens free text.
    if (field === "couplant" && itemId === COUPLANT_OTHER) {
      this.report.couplantId = null;
      this.report.couplant = "";
      this.couplantOtherOpen = true;
      this.scheduleAutosave();
      this.render();
      return;
    }
    if (field === "couplant") this.couplantOtherOpen = false;

    const source = field === "referenceNorm" ? this.norms : field === "acceptanceCriteria" ? this.criteria : this.couplants;
    const item = source.find(i => i.id === itemId);

    if (field === "referenceNorm") {
      this.report.referenceNormId = itemId || null;
      // The document carries the registered code alone.
      this.report.referenceNorm = item ? formatCatalogItemCode(item) : "";
    } else if (field === "acceptanceCriteria") {
      this.report.acceptanceCriteriaId = itemId || null;
      this.report.acceptanceCriteria = item ? formatCatalogItemLabel(item) : "";
    } else {
      this.report.couplantId = itemId || null;
      this.report.couplant = item ? formatCatalogItemLabel(item) : "";
    }

    this.scheduleAutosave();
    this.render();
  }

  private async onCalibrationBlockChange(assetId: string) {
    if (!this.report) return;
    const block = this.calibrationBlocks.find(a => a.id === assetId);
    this.report.calibrationBlockAssetId = assetId || null;

    if (!block) {
      this.report.calibrationBlock = "";
      this.report.calibrationBlockMaterial = "";
      this.report.calibrationBlockRange = "";
      this.report.calibrationBlockCert = "";
      this.report.calibrationBlockCertDate = "";
      this.report.calibrationBlockCertValidity = "";
      this.report.calibrationBlockCertLaboratory = "";
    } else {
      this.report.calibrationBlock = [block.internalCode, block.serialNumber].filter(Boolean).join(" / ");
      this.report.calibrationBlockMaterial = block.technicalData?.material || "";
      this.report.calibrationBlockRange = formatCalibrationBlockRange(block);
      // Frozen onto the report: the certificate as it stands right now.
      const cert = await this.latestCertificateOf(block.id);
      this.report.calibrationBlockCert = cert?.certificateNumber || "";
      this.report.calibrationBlockCertDate = cert?.issueDate || "";
      this.report.calibrationBlockCertValidity = cert?.expirationDate || "";
      this.report.calibrationBlockCertLaboratory = cert?.laboratory || "";
    }

    this.scheduleAutosave();
    this.render();
  }

  private async onDeviceAssetChange(assetId: string) {
    if (!this.report) return;
    const device = this.devices.find(a => a.id === assetId);
    this.report.deviceAssetId = assetId || null;

    if (!device) {
      this.report.deviceManufacturer = "";
      this.report.deviceModel = "";
      this.report.deviceSerialNumber = "";
      this.report.deviceCalibrationCert = "";
      this.report.deviceCalibrationDate = "";
      this.report.deviceCalibrationValidity = "";
      this.report.deviceCalibrationLaboratory = "";
    } else {
      this.report.deviceManufacturer = device.manufacturer || "";
      this.report.deviceModel = device.model || "";
      this.report.deviceSerialNumber = device.serialNumber || "";
      // Frozen onto the report: the certificate as it stands right now.
      const cert = await this.latestCertificateOf(device.id);
      this.report.deviceCalibrationCert = cert?.certificateNumber || "";
      this.report.deviceCalibrationDate = cert?.issueDate || "";
      this.report.deviceCalibrationValidity = cert?.expirationDate || "";
      this.report.deviceCalibrationLaboratory = cert?.laboratory || "";
    }

    this.scheduleAutosave();
    this.render();
  }

  private async onTransducerAssetChange(assetId: string) {
    if (!this.report) return;
    const transducer = this.transducers.find(a => a.id === assetId);
    this.report.transducerAssetId = assetId || null;

    if (!transducer) {
      this.report.transducerManufacturer = "";
      this.report.transducerModel = "";
      this.report.transducerSerialNumber = "";
      this.report.transducerIdentification = "";
      this.report.transducerType = "";
      this.report.transducerFrequency = "";
      this.report.transducerDimension = "";
      this.report.transducerDimensionFrequency = "";
    } else {
      const td = transducer.technicalData || {};
      const dimension = formatTransducerDimension(td.elementDiameter, td.dimensionUnit) || td.elementDimensions || "";
      const frequency = formatTransducerFrequency(td.frequencyMHz);
      this.report.transducerManufacturer = transducer.manufacturer || "";
      this.report.transducerModel = transducer.model || "";
      this.report.transducerSerialNumber = transducer.serialNumber || "";
      this.report.transducerIdentification = transducer.internalCode || "";
      this.report.transducerType = td.transducerType || "";
      this.report.transducerFrequency = frequency;
      this.report.transducerDimension = dimension;
      this.report.transducerDimensionFrequency = [dimension, frequency].filter(Boolean).join(" / ");
    }

    this.scheduleAutosave();
    this.render();
  }

  /**
   * Selecting a registered procedure fills the parameters that come from it.
   * Fields the user already typed are preserved — the selection never
   * silently overwrites manual content, and "Comentários e Observações"
   * only receives the procedure's standard text while still empty (the ME
   * report's rule is that this field *starts* empty and is never machine
   * generated behind the user's back).
   */
  private onProcedureChange(procedureId: string) {
    if (!this.report) return;
    const procedure = this.procedures.find(p => p.id === procedureId);

    this.report.procedureId = procedureId || null;
    if (!procedure) {
      this.scheduleAutosave();
      this.render();
      return;
    }

    this.report.procedureRevision = formatProcedureLabel(procedure);

    // The procedure may pre-select Norma/Critério: when its registered value
    // matches a catalog record, bind that record; otherwise keep the
    // procedure's own text without pretending a catalog link exists.
    if (procedure.referenceStandard) {
      const match = this.matchCatalogItem(this.norms, procedure.referenceStandard);
      this.report.referenceNormId = match?.id ?? null;
      this.report.referenceNorm = match ? formatCatalogItemCode(match) : procedure.referenceStandard;
    }
    if (procedure.acceptanceCriteria) {
      const match = this.matchCatalogItem(this.criteria, procedure.acceptanceCriteria);
      this.report.acceptanceCriteriaId = match?.id ?? null;
      this.report.acceptanceCriteria = match ? formatCatalogItemLabel(match) : procedure.acceptanceCriteria;
    }
    if (procedure.defaultTechnique) {
      this.report.measurementTechnique = procedure.defaultTechnique;
      if (!isOtherTechnique(procedure.defaultTechnique)) this.report.measurementTechniqueOther = "";
    }
    if (procedure.observations && !this.report.comments.trim()) {
      this.report.comments = procedure.observations;
    }

    this.scheduleAutosave();
    this.render();
  }

  /** Matches a procedure's registered text against a catalog record, by code, name or full label. */
  /**
   * Binds the report to an OS/Sub-OS from the official registry. The code is
   * never typed — it is copied from the chosen record and frozen here.
   */
  private async onReportWorkOrderChange(workOrderId: string) {
    if (!this.report) return;
    const workOrder = this.workOrders.find((w: any) => w.id === workOrderId);

    this.report.osWorkOrderId = workOrderId || null;
    this.report.osNumber = workOrder?.code || "";
    this.report.osSubWorkOrderId = null;
    this.report.subOsNumber = "";
    this.report.osSubOs = this.report.osNumber;

    this.selection.workOrderId = workOrderId;
    await this.loadSubWorkOrders();
    this.scheduleAutosave();
    this.render();
  }

  private onReportSubWorkOrderChange(subWorkOrderId: string) {
    if (!this.report) return;
    const sub = this.subWorkOrders.find((s: any) => s.id === subWorkOrderId);

    this.report.osSubWorkOrderId = subWorkOrderId || null;
    this.report.subOsNumber = sub?.code || "";
    this.report.osSubOs = [this.report.osNumber, this.report.subOsNumber].filter(Boolean).join(" / ");

    this.scheduleAutosave();
    this.render();
  }

  private matchCatalogItem(items: NdtCatalogItem[], value: string): NdtCatalogItem | undefined {
    const target = value.trim().toLowerCase();
    if (!target) return undefined;
    return items.find(i =>
      i.name.trim().toLowerCase() === target ||
      (!!i.code && i.code.trim().toLowerCase() === target) ||
      formatCatalogItemLabel(i).trim().toLowerCase() === target
    );
  }

  private onTechniqueChange(technique: string) {
    if (!this.report) return;
    this.report.measurementTechnique = technique;
    if (!isOtherTechnique(technique)) this.report.measurementTechniqueOther = "";
    this.scheduleAutosave();
    this.render();
  }

  // --- CONTEXTO DO RELATÓRIO (Cliente → Projeto → Unidade/Área → Equipamento → Inspeção) ---

  private async loadClients() {
    const ctx = this.buildCtx();
    const clientsLoaded = await this.ctxLoad.load("Clientes", () => window.app.platform.adminService.listAuthorizedClients(ctx));
    this.clients = clientsLoaded as any[];

    // Global active client is a pre-selection only (never a requirement).
    const preselected = contextStore.get().activeClientId || "";
    if (preselected && this.clients.some((c: any) => c.id === preselected)) {
      this.selection.clientId = preselected;
      await this.loadProjects();
    }
  }

  private async loadProjects() {
    this.projects = [];
    this.unitAreas = [];
    this.equipments = [];
    this.inspections = [];
    if (!this.selection.clientId) return;
    const projectsLoaded = await this.ctxLoad.load("Projetos", () =>
      window.app.platform.projectService?.listProjects(this.selection.clientId) ?? Promise.resolve([]));
    this.projects = projectsLoaded as any[];
  }

  private async loadUnitAreas() {
    this.unitAreas = [];
    this.equipments = [];
    this.inspections = [];
    if (!this.selection.projectId) return;
    const unitAreasLoaded = await this.ctxLoad.load("Unidades / Áreas", () =>
      window.app.platform.projectService?.listUnitAreas({ projectId: this.selection.projectId, clientId: this.selection.clientId }) ?? Promise.resolve([]));
    this.unitAreas = unitAreasLoaded as any[];
  }

  private async loadEquipments() {
    this.equipments = [];
    this.inspections = [];
    if (!this.selection.clientId) return;
    const equipmentsLoaded = await this.ctxLoad.load("Equipamentos", () =>
      window.app.platform.equipmentService.listEquipments(this.scopedCtx(), { clientId: this.selection.clientId }));
    this.equipments = filterEquipmentsByContext(equipmentsLoaded as any[], this.selection);
  }

  private async loadWorkOrders() {
    this.workOrders = [];
    this.subWorkOrders = [];
    if (!this.selection.clientId) return;
    const workOrdersLoaded = await this.ctxLoad.load("Ordens de Serviço", () =>
      window.app.platform.adminService.listWorkOrdersForDocument(this.buildCtx(), this.selection.clientId, this.selection.projectId || undefined));
    this.workOrders = workOrdersLoaded as any[];
  }

  private async loadSubWorkOrders() {
    this.subWorkOrders = [];
    if (!this.selection.workOrderId) return;
    const subWorkOrdersLoaded = await this.ctxLoad.load("Sub-OS", () =>
      window.app.platform.adminService.listSubWorkOrdersForDocument(this.buildCtx(), this.selection.workOrderId));
    this.subWorkOrders = subWorkOrdersLoaded as any[];
  }

  private async loadInspections() {
    this.inspections = [];
    if (!this.selection.equipmentId) return;
    const inspectionsLoaded = await this.ctxLoad.load("Inspeções", () =>
      window.app.platform.inspectionService.listInspections(this.scopedCtx(), this.selection.equipmentId as any));
    this.inspections = inspectionsLoaded as any[];
  }

  /**
   * Resolves the ME already bound to the selected Inspection. A failure here
   * must never degrade silently into "no report found" — that would offer
   * "Criar Relatório ME" for an Inspection that already has one, and the
   * creation would then be refused by the service.
   */
  private async resolveExistingReport() {
    this.existingReport = null;
    if (!this.selection.inspectionId) return;
    try {
      this.existingReport = await window.app.platform.meReportService
        .getByInspection(this.scopedCtx(), this.selection.inspectionId);
    } catch (e: any) {
      console.error("[ME REPORT] Falha ao verificar se já existe Relatório ME para a inspeção", e);
      this.existingReport = null;
      this.contextError = e instanceof AppError
        ? ((e as any).safeMessage || e.message)
        : "Não foi possível verificar se já existe um Relatório ME para esta Inspeção.";
    }
  }

  private async onContextChange(field: keyof AppMeReportPage["selection"], value: string) {
    this.contextError = null;
    this.selection[field] = value;

    // Downstream selections are invalidated whenever an upstream one changes.
    if (field === "clientId") {
      this.selection.projectId = "";
      this.selection.unitAreaId = "";
      this.selection.equipmentId = "";
      this.selection.inspectionId = "";
      this.selection.workOrderId = "";
      this.selection.subWorkOrderId = "";
      await this.loadProjects();
      await this.loadEquipments();
      await this.loadWorkOrders();
    } else if (field === "projectId") {
      this.selection.unitAreaId = "";
      this.selection.equipmentId = "";
      this.selection.inspectionId = "";
      this.selection.workOrderId = "";
      this.selection.subWorkOrderId = "";
      await this.loadUnitAreas();
      await this.loadEquipments();
      await this.loadWorkOrders();
    } else if (field === "workOrderId") {
      this.selection.subWorkOrderId = "";
      await this.loadSubWorkOrders();
    } else if (field === "unitAreaId") {
      this.selection.equipmentId = "";
      this.selection.inspectionId = "";
      await this.loadEquipments();
    } else if (field === "equipmentId") {
      this.selection.inspectionId = "";
      await this.loadInspections();
    }

    await this.resolveExistingReport();
    this.render();
  }

  private buildSeedFromSelection(): MeContextSeed {
    const inspection = this.inspections.find((i: any) => i.id === this.selection.inspectionId);
    return buildMeSeedFromContext({
      selection: this.selection,
      client: this.clients.find((c: any) => c.id === this.selection.clientId),
      project: this.projects.find((p: any) => p.id === this.selection.projectId),
      unitArea: this.unitAreas.find((u: any) => u.id === this.selection.unitAreaId),
      equipment: this.equipments.find((e: any) => e.id === this.selection.equipmentId),
      inspection,
      inspector: inspection ? this.professionals.find((p: any) => p.id === inspection.inspectorId) : undefined,
      equipmentTypeLabels: EQUIPMENT_TYPE_LABELS
    });
  }

  private async onCreateFromContext() {
    if (this.isResolvingContext) return;
    const missing = this.missingContextFields();
    if (missing.length > 0) {
      this.contextError = `Selecione ${missing.join(", ")} para criar o Relatório ME.`;
      this.render();
      return;
    }

    // Guard against acting on ids whose option lists have since been
    // reloaded — the selection must match what the dropdowns actually show.
    const stale = staleMeContextFields(this.selection, {
      clients: this.clients,
      projects: this.projects,
      unitAreas: this.unitAreas,
      equipments: this.equipments,
      inspections: this.inspections
    });
    if (stale.length > 0) {
      this.contextError = `Selecione novamente ${stale.join(", ")} — o contexto foi recarregado.`;
      this.render();
      return;
    }

    this.isResolvingContext = true;
    this.contextError = null;
    this.render();

    try {
      const seed = this.buildSeedFromSelection();
      const created = await window.app.platform.meReportService.createDraft(this.scopedCtx(), seed);
      /** Houve complemento (signatário e/ou OS) a gravar depois da criação? */
      let complementApplied = false;

      // Bind the Inspetor as the INSPETOR signatory straight from the
      // Inspection's Professional — never free text.
      if (seed.inspectorProfessionalId) {
        const prof = this.professionals.find((p: any) => p.id === seed.inspectorProfessionalId);
        if (prof) {
          created.signatories = [{
            role: "INSPETOR",
            professionalId: prof.id,
            name: prof.name || "",
            functionTitle: prof.functionTitle || "INSPETOR DE EQUIPAMENTOS",
            credential: this.credentialFor(prof),
            signatureImageId: prof.signatureImageId || null
          }];
          complementApplied = true;
        }
      }

      // OS/Sub-OS come from the official registry selected in the context —
      // never typed into the report — and are frozen onto it.
      const workOrder = this.workOrders.find((w: any) => w.id === this.selection.workOrderId);
      const subWorkOrder = this.subWorkOrders.find((s: any) => s.id === this.selection.subWorkOrderId);
      if (workOrder || subWorkOrder) {
        created.osWorkOrderId = workOrder?.id || null;
        created.osSubWorkOrderId = subWorkOrder?.id || null;
        created.osNumber = workOrder?.code || "";
        created.subOsNumber = subWorkOrder?.code || "";
        created.osSubOs = [created.osNumber, created.subOsNumber].filter(Boolean).join(" / ");
        complementApplied = true;
      }

      /**
       * UMA gravação para os dois complementos, e a falha APARECE.
       *
       * Eram duas chamadas `saveDraft(...).catch(() => {})` seguidas. Se
       * qualquer uma falhasse — e a Sub-OS incompatível é recusada de
       * propósito pelo servidor (§34.5) — o relatório ficava gravado SEM o
       * signatário ou SEM a OS/Sub-OS, e a tela seguia exibindo
       * `created`, o objeto de memória que JÁ tinha os campos preenchidos.
       * A pessoa via OS e Inspetor na tela e o armazenamento não tinha
       * nenhum dos dois; sumiam no primeiro F5.
       *
       * Agora o que a tela mostra é o que voltou do serviço. Falhou: diz o
       * que não foi gravado e mostra o estado REAL.
       */
      this.report = created;
      if (complementApplied) {
        try {
          this.report = await window.app.platform.meReportService.saveDraft(this.scopedCtx(), created);
        } catch (e: unknown) {
          const detail = e instanceof AppError
            ? ((e as { safeMessage?: string }).safeMessage || e.message)
            : ((e as { message?: string })?.message || "motivo não informado");
          console.error("[ME REPORT] Falha ao gravar Inspetor/OS no relatório recém-criado", e);
          this.saveNotice = `RELATÓRIO CRIADO, MAS O VÍNCULO DE INSPETOR/OS NÃO FOI GRAVADO — ${detail} Confira e selecione novamente antes de preencher.`;
          const persisted = await window.app.platform.meReportService
            .getReport(this.scopedCtx(), created.id).catch(() => null);
          if (persisted) this.report = persisted;
        }
      }
      const created2 = this.report;
      this.pointSeq = this.computePointSeq(created2.points);
      await this.loadProcedures();
      await this.loadTechnicalSources();
      await this.hydrateContextFromReport();
      this.isResolvingContext = false;
      window.history.replaceState(null, "", `/ndt/thickness/${created2.id}`);
      this.render();
    } catch (e: any) {
      console.error("[ME REPORT] Falha ao criar relatório a partir do contexto", e);
      this.isResolvingContext = false;
      const message = e instanceof AppError ? ((e as any).safeMessage || e.message) : (e?.message || "Erro ao criar o Relatório ME.");
      await this.resolveExistingReport();
      // The most common cause is a report that already exists for this
      // Inspection — recover into the "abrir o existente" path instead of
      // dead-ending on the error.
      this.contextError = this.existingReport
        ? `Esta Inspeção já possui o Relatório ME Nº ${this.existingReport.reportNumber}. Use "Abrir Relatório" para continuar o preenchimento.`
        : message;
      this.render();
    }
  }

  private async onOpenExisting() {
    if (!this.existingReport) return;
    this.report = this.existingReport;
    this.pointSeq = this.computePointSeq(this.report.points);
    this.equipments = await window.app.platform.equipmentService
      .listEquipments(this.scopedCtx(this.report.clientId), { clientId: this.report.clientId })
      .catch(() => []);
    await this.loadProcedures();
    await this.loadTechnicalSources();
    await this.hydrateContextFromReport();
    window.history.replaceState(null, "", `/ndt/thickness/${this.report.id}`);
    this.render();
  }

  private missingContextFields(): string[] {
    return missingMeContextFields(this.selection);
  }

  private computePointSeq(points: MeResultPoint[]): number {
    let max = 0;
    for (const p of points) {
      const n = parseInt((p.id || "").replace(/[^0-9]/g, ""), 10);
      if (!isNaN(n) && n > max) max = n;
    }
    return max;
  }

  /**
   * AUTOSAVE — a falha PRECISA aparecer.
   *
   * Era `this.persistNow().catch(() => {})`: a gravação automática falhava
   * em silêncio absoluto. Cota de armazenamento estourada, permissão
   * negada, Sub-OS incompatível recusada pelo servidor — nada disso
   * chegava à tela. A pessoa seguia digitando por horas acreditando que o
   * relatório estava salvo, e perdia tudo no primeiro F5.
   *
   * Um autosave que falha calado é pior que autosave nenhum: sem ele a
   * pessoa ao menos sabe que precisa salvar.
   */
  private scheduleAutosave() {
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    // CONFIGURAÇÕES → GERAL: autosave desligado = só grava em "Salvar rascunho".
    const delay = autosaveDelayMs();
    if (delay === null) return;
    this.autosaveTimer = setTimeout(() => {
      void this.persistNow().then(() => {
        // Só limpa o aviso se ele for de uma falha ANTERIOR do autosave:
        // apagar uma mensagem de fluxo ("enviado ao PLH") seria engolir
        // informação que a pessoa ainda não leu.
        if (this.autosaveFailed) {
          this.autosaveFailed = false;
          this.saveNotice = null;
          this.render();
        }
      }).catch((e: unknown) => {
        this.autosaveFailed = true;
        const detail = e instanceof AppError
          ? ((e as { safeMessage?: string }).safeMessage || e.message)
          : ((e as { message?: string })?.message || "motivo não informado");
        this.saveNotice = `NÃO FOI POSSÍVEL SALVAR AUTOMATICAMENTE — ${detail} As alterações mais recentes NÃO estão gravadas. Use SALVAR para tentar de novo antes de sair da tela.`;
        console.error("[ME REPORT] Falha no salvamento automático", e);
        this.render();
      });
    }, delay);
  }

  private async persistNow(): Promise<void> {
    if (!this.report) return;
    const ctx = this.buildCtx();
    try {
      this.isSaving = true;
      const saved = await window.app.platform.meReportService.saveDraft(ctx, this.report);
      this.report = saved;
      this.isSaving = false;
    } catch (e: any) {
      this.isSaving = false;
      throw e;
    }
  }

  private handleFormInput(e: Event) {
    if (!this.report) return;
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (!target || !("dataset" in target)) return;

    const field = target.dataset["field"];
    const pointId = target.dataset["pointId"];
    const pointField = target.dataset["pointField"];
    const sigRole = target.dataset["sigRole"];
    const sigField = target.dataset["sigField"];

    if (target.dataset["laudoDecision"] && pointId) {
      this.onLaudoDecision(pointId, target.value);
      return;
    }

    if (pointId && pointField) {
      const p = this.report.points.find(pt => pt.key === pointId);
      if (p) {
        if (pointField === "nominalThickness" || pointField === "minThickness" || pointField === "actualThickness") {
          // Stored as a number; "6,20" is only how it is shown.
          (p as any)[pointField] = parseDecimalBR(target.value);
          // The laudo derives from the thicknesses, so it updates as they are typed.
          this.refreshLaudoCell(p);
        } else {
          (p as any)[pointField] = target.value;
        }
      }
      this.scheduleAutosave();
      return;
    }

    if (sigRole && sigField) {
      const s = this.report.signatories.find(sg => sg.role === sigRole);
      if (s) (s as any)[sigField] = target.value;
      this.scheduleAutosave();
      return;
    }

    if (field && target.dataset["managed"] !== "true") {
      (this.report as any)[field] = target.value;
      // O material ESPECÍFICO decide calibrações qualificadas por especificação.
      if (field === "materialSpecification") this.refreshHotCells();
      this.scheduleAutosave();
    }
  }

  /**
   * FAMÍLIA DE MATERIAL e TEMPERATURAS vindas do combobox.
   *
   * Grava o código canônico (e o rótulo congelado, no caso do material),
   * recalcula a correção térmica e re-renderiza — cruzar os 60 °C muda a
   * tabela inteira, então aqui não dá para atualizar só uma célula.
   */
  private handleSearchableChange(event: Event): void {
    if (!this.report) return;
    const detail = (event as CustomEvent<{ value: string; label: string; field: string }>).detail;
    if (!detail?.field) return;

    switch (detail.field) {
      case "materialFamilyCode":
        this.report.materialFamilyCode = detail.value || null;
        this.report.materialFamilyLabel = detail.label || "";
        break;
      case "measurementTemperatureC":
        this.report.measurementTemperatureC = detail.value === "" ? null : Number(detail.value);
        break;
      default:
        return;
    }

    // Trocar grupo/material ou TME invalida o FC anterior: o render resolve
    // a calibração de novo (recomputeHot) — nunca reaproveita o FC antigo.
    this.scheduleAutosave();
    this.render();
  }

  /**
   * Aplica a cascata Inspetor → Sub-OS ao relatório, congelando o código da
   * Sub-OS escolhida. Uma seleção que não pertença mais ao Inspetor é
   * limpa — é o ponto da regra.
   */
  private applySubOsForInspector(): void {
    if (!this.report) return;
    const r = this.report;
    const resolution = resolveMeSubOs(this.subWorkOrders, r.inspectorProfessionalId, r.osSubWorkOrderId);

    if (resolution.selectedId === null) {
      r.osSubWorkOrderId = null;
      r.subOsNumber = "";
    } else {
      const chosen = resolution.options.find(o => o.id === resolution.selectedId);
      r.osSubWorkOrderId = resolution.selectedId;
      r.subOsNumber = (chosen as any)?.code || "";
    }
    r.osSubOs = [r.osNumber, r.subOsNumber].filter(Boolean).join(" / ");
  }

  /**
   * Inspetor automático para quem não pode designar outro.
   *
   * Roda ao abrir um relatório ainda editável e sem Inspetor definido. Não
   * sobrescreve um Inspetor já gravado: um ME criado pela Engenharia em nome
   * de alguém não pode ser sequestrado por quem abrir a tela depois.
   */
  private applyAutomaticInspector(): void {
    if (!this.report || this.canAssignInspector()) return;
    if (this.report.inspectorProfessionalId) return;
    if (isMeClosed(this.report.status)) return;

    const own = automaticInspectorId(contextStore.get().professionalId);
    if (!own) return;
    this.onInspectorChange(own);
  }

  private ensureSignatory(role: MeSignatory["role"]): MeSignatory {
    if (!this.report) throw new Error("no report");
    let s = this.report.signatories.find(sg => sg.role === role);
    if (!s) {
      s = { role, professionalId: null, name: "", functionTitle: "", credential: "", signatureImageId: null };
      this.report.signatories.push(s);
    }
    return s;
  }

  private credentialFor(p: any): string {
    return p.crea || p.cft || p.snqc || p.sncc || p.endIso9712 || "";
  }

  /**
   * The context binding (Cliente/Projeto/Unidade/Equipamento/Inspeção) is
   * never changed silently on a report that already exists — the user is sent
   * back to the context selector, where they open the ME of the chosen
   * Inspection or create a separate one.
   */
  private async onChangeContext() {
    const confirmed = window.confirm(
      "Trocar o contexto abre a seleção de Cliente/Projeto/Unidade/Equipamento/Inspeção.\n\n" +
      "O vínculo deste relatório não é alterado: você abrirá o Relatório ME da inspeção escolhida ou criará outro. Continuar?"
    );
    if (!confirmed) return;

    this.report = null;
    this.loadError = null;
    this.saveNotice = null;
    this.validationErrors = null;
    this.existingReport = null;
    this.contextError = null;
    // The whole selection is rebuilt from scratch. Keeping the previous ids
    // while their option lists are reloaded would leave the page holding a
    // selection the dropdowns no longer show — and "Criar Relatório ME"
    // would then be offered for an Inspection that already has one.
    this.selection = { clientId: "", projectId: "", unitAreaId: "", equipmentId: "", inspectionId: "", workOrderId: "", subWorkOrderId: "" };
    this.workOrders = [];
    this.subWorkOrders = [];
    this.projects = [];
    this.unitAreas = [];
    this.equipments = [];
    this.inspections = [];
    this.removeAttribute("id");
    window.history.replaceState(null, "", "/ndt/thickness");
    this.isLoading = true;
    this.render();
    await this.loadClients();
    await this.loadEquipments();
    await this.resolveExistingReport();
    this.isLoading = false;
    this.render();
  }

  private onInspectorChange(professionalId: string) {
    if (!this.report) return;
    const prof = this.professionals.find(p => p.id === professionalId);
    this.report.inspectorProfessionalId = (professionalId as any) || null;
    this.report.inspectorName = prof?.name || "";
    const insp = this.ensureSignatory("INSPETOR");
    insp.professionalId = (professionalId as any) || null;
    insp.name = prof?.name || "";
    insp.functionTitle = prof?.functionTitle || "INSPETOR DE EQUIPAMENTOS";
    insp.credential = prof ? this.credentialFor(prof) : "";
    insp.signatureImageId = prof?.signatureImageId || null;
    // Trocar de Inspetor RECALCULA a Sub-OS. Sem isso o relatório continuaria
    // apontando para a Sub-OS de outra pessoa — vínculo errado, sem aviso.
    this.applySubOsForInspector();
    this.scheduleAutosave();
    this.render();
  }

  // `onPlhChange` foi REMOVIDO junto com o seletor manual de PLH. O PLH do
  // documento é o responsável nomeado pelo Cliente (CLAUDE.md, seções 24 e
  // 28) e é resolvido no serviço, ao gravar. Escolher à mão permitia anunciar
  // no PDF um aprovador que o gate de aprovação recusaria.

  private addPoint() {
    if (!this.report) return;
    // The id is only a suggestion — the user may rename it to whatever the
    // croqui uses, and it is never regenerated afterwards.
    this.report.points.push({
      key: `PT-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      id: suggestNextPointId(this.report.points.map(p => p.id)),
      component: "",
      location: "",
      nominalThickness: null,
      minThickness: null,
      actualThickness: null,
      laudo: null
    });
    this.scheduleAutosave();
    this.render();
  }

  private removePoint(key: string) {
    if (!this.report) return;
    this.report.points = this.report.points.filter(p => p.key !== key);
    this.scheduleAutosave();
    this.render();
  }

  private async onCroquiFile(file: File) {
    if (!this.report) return;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    this.report.croquiImage = dataUrl;
    this.scheduleAutosave();
    this.render();
  }

  private async onSaveDraft() {
    if (!this.report) return;
    try {
      await this.persistNow();
      this.saveNotice = "Rascunho salvo com sucesso.";
      this.validationErrors = null;
      this.validationOk = false;
    } catch (e: any) {
      this.saveNotice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Erro ao salvar rascunho.";
    }
    this.render();
  }

  private async onValidate() {
    if (!this.report) return;
    const ctx = this.buildCtx();
    try {
      await this.persistNow();
      const result = await window.app.platform.meReportService.validate(ctx, this.report.id);
      this.validationOk = result.valid;
      this.validationErrors = result.valid ? [] : result.errors;
      this.saveNotice = null;
    } catch (e: any) {
      this.validationErrors = [e instanceof AppError ? ((e as any).safeMessage || e.message) : "Erro ao validar dados."];
      this.validationOk = false;
    }
    this.render();
  }

  /**
   * VISUALIZAR RELATÓRIO / GERAR PDF — leitura, nunca gravação.
   *
   * Salvar antes de montar só faz sentido enquanto o documento é editável:
   * é o que garante que a prévia reflita o que está na tela. Num ME
   * finalizado (ou em análise do PLH) o salvamento é recusado pelo serviço,
   * e o `persistNow()` incondicional transformava um clique em "Visualizar"
   * na mensagem "Relatório ME finalizado não pode mais ser editado" — ou
   * seja, justamente quem precisa ler o documento aprovado não conseguia.
   */
  private async onPreviewOrPdf() {
    if (!this.report) return;
    const ctx = this.buildCtx();
    const editable = !isMeClosed(this.report.status) && !reviewEditingLocked(this.review) && !ndtHoldLocked(this.engineering);
    try {
      if (editable) await this.persistNow();
      this.previewSnapshot = await window.app.platform.meReportService.buildDocumentSnapshot(ctx, this.report.id);
      this.previewOpen = true;
      this.render();
      const preview = this.querySelector("#me-pdf-preview") as any;
      if (preview) preview.snapshot = this.previewSnapshot;
    } catch (e: any) {
      this.saveNotice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Erro ao gerar prévia do relatório.";
      this.render();
    }
  }

  private async onSignAsPlh() {
    if (!this.report) return;
    const ctx = this.buildCtx();
    try {
      const signed = await window.app.platform.meReportService.signAsPlh(ctx, this.report.id);
      this.report = signed;
      this.saveNotice = "Relatório assinado como PLH com sucesso.";
    } catch (e: any) {
      this.saveNotice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Erro ao assinar como PLH.";
    }
    this.render();
  }

  private closePreview() {
    this.previewOpen = false;
    this.render();
  }

  // ---------------------------------------------------------------------

  render() {
    const ctx = contextStore;
    const has = (p: string) => ctx.hasPermission(p);

    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 56px 24px; display: flex; flex-direction: column; align-items: center; gap: 14px; color: var(--color-text-disabled);">
          <svg class="spinner spinner-lg" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
          <span>Carregando Relatório ME...</span>
        </div>`;
      return;
    }

    if (!this.report && !this.loadError) {
      this.renderContextSelection();
      return;
    }

    if (this.loadError || !this.report) {
      this.innerHTML = `
        <div class="me-empty-state">
          <h2>RELATÓRIO DE MEDIÇÃO DE ESPESSURA (ME)</h2>
          <p style="color: var(--status-danger-solid);">${this.escapeHtml(this.loadError || "Erro desconhecido.")}</p>
          <div style="display:flex; gap:10px;">
            <button class="btn-action" id="btn-retry-load">Tentar novamente</button>
            <button class="btn-action" id="btn-back-thickness">Voltar</button>
          </div>
        </div>
        <style>${this.styles()}</style>`;
      this.querySelector("#btn-retry-load")?.addEventListener("click", () => {
        this.loadError = null;
        this.isLoading = true;
        this.init();
      });
      this.querySelector("#btn-back-thickness")?.addEventListener("click", () => {
        router.navigate("/ndt/thickness");
      });
      return;
    }

    const r = this.report;
    const reviewLocked = reviewEditingLocked(this.review) || ndtHoldLocked(this.engineering);
    const canEdit = has(PermissionCode.MeReportEdit) && r.status !== "SIGNED" && !reviewLocked;
    const canValidate = has(PermissionCode.MeReportValidate) && !reviewLocked;
    const canGeneratePdf = has(PermissionCode.MeReportGeneratePdf);
    const canSign = has(PermissionCode.MeReportApprove) && has(PermissionCode.MeReportSign) && !ndtHoldLocked(this.engineering);
    const readOnly = !canEdit;
    if (canEdit) this.recomputeHot();

    this.innerHTML = `
      <style>${this.styles()}</style>

      <div class="me-breadcrumb">
        <span class="crumb-link" id="crumb-reports">Relatórios</span>
        <span>›</span>
        <span class="crumb-link" id="crumb-thickness">Medição de Espessura (ME)</span>
        <span>›</span>
        <span>${r.reportNumber ? "Relatório" : "Novo Relatório"}</span>
      </div>

      <div class="me-header">
        <div>
          <h1>Relatório de Inspeção de Medição de Espessura (ME)</h1>
          <p>Preenchimento dos dados técnicos do ensaio e resultados de medição.</p>
        </div>
        <div class="me-header-meta">
          <div class="meta-item"><span class="meta-lbl">TAG</span><span class="meta-val">${this.escapeHtml(r.equipmentTag || "-")}</span></div>
          <div class="meta-item"><span class="meta-lbl">Nº DO RELATÓRIO</span><span class="meta-val">${this.escapeHtml(r.reportNumber)}</span></div>
          <div class="meta-item"><span class="meta-lbl">REVISÃO</span><span class="meta-val">Rev. ${this.escapeHtml(r.revision)}</span></div>
          <div class="meta-item"><span class="meta-lbl">STATUS</span><span class="status-badge ${this.statusBadgeClass(r.status)}">${this.engineering.hold ? "EM HOLD" : this.statusLabel(r.status)}</span></div>
        </div>
      </div>

      <div class="me-actionbar">
        ${canEdit ? `<button class="btn-action" id="btn-save-draft" ${this.isSaving ? "disabled" : ""}><i data-lucide="save"></i> Salvar Rascunho</button>` : ""}
        <button class="btn-action" id="btn-preview"><i data-lucide="eye"></i> Visualizar Relatório</button>
        ${canValidate ? `<button class="btn-action" id="btn-validate"><i data-lucide="check-circle"></i> Validar Dados</button>` : ""}
        ${canGeneratePdf ? `<button class="btn-action primary" id="btn-generate-pdf"><i data-lucide="file-down"></i> Gerar PDF</button>` : ""}
        ${this.renderReleaseButton(r)}
        ${ndtHoldButton(this.engineering, has(PermissionCode.MeReportEdit) && r.status !== "SIGNED" && !reviewEditingLocked(this.review), "btn-action")}
        <button class="btn-action" id="btn-change-context" title="Selecionar outro contexto documental (Cliente/Projeto/Equipamento/Inspeção)">Trocar Contexto</button>
      </div>

      ${this.renderReleaseConfirmModal()}
      ${this.renderReleaseBlockedPanel()}
      ${plhApproveConfirmModal(this.showApproveConfirm, this.approving, this.approveFailure)}
      <!--
        CARD ANÁLISE DO PLH — o painel global do workflow (Integridade, END e
        AVR usam o mesmo). Substitui os dois botões soltos da barra de ações:
        eles simplesmente sumiam quando alguma condição falhava, e quem abria
        o relatório via um card vazio sem saber se era falta de permissão,
        PLH de outro Cliente ou documento ainda não liberado. O painel mostra
        o estado, o PLH responsável e — quando não há ação — o MOTIVO.
      -->
      ${this.review || this.engineering.hold ? plhApprovalPanel(this.review, { hold: !!this.engineering.hold }) : ""}
      ${aiReviewSlot("ME", r.id, `Relatório ME ${r.reportNumber || ""}`)}
      ${reviewBanner(this.review)}
      ${ndtEngineeringBanners(this.engineering, canResolveCorrections())}
      ${this.saveNotice ? `<div class="me-notice">${this.escapeHtml(this.saveNotice)}</div>` : ""}
      ${this.validationErrors !== null ? this.renderValidationBanner() : ""}

      <div class="me-layout">
        <nav class="me-nav">
          ${["01|Identificação", "02|Parâmetros", "03|Aparelho / Transdutor", "04|Resultados", "05|Critérios", "06|Croqui", "07|Observações", "08|Signatários"].map(entry => {
            const [num, label] = entry.split("|");
            return `<a href="#me-sec-${num}" class="me-nav-item" data-sec="${num}"><span class="nav-num">${num}</span>${label}</a>`;
          }).join("")}
        </nav>

        <div class="me-main">
          <div class="me-col me-col-main">
            ${this.renderCardIdentification(r, readOnly)}
            ${this.renderCardParameters(r, readOnly)}
            ${this.renderCardEquipment(r, readOnly)}
            ${this.renderCardResults(r, readOnly)}
          </div>
          <div class="me-col me-col-side">
            ${this.renderCardLegend()}
            ${this.renderCardCroqui(r, readOnly)}
            ${this.renderCardObservations(r, readOnly)}
            ${this.renderCardSignatories(r, readOnly, canSign)}
          </div>
        </div>
      </div>

      <div class="pdf-modal-container" style="display:${this.previewOpen ? "flex" : "none"};" id="me-pdf-modal">
        <div class="pdf-modal-content">
          <div class="pdf-modal-header">
            <div style="font-weight:600; display:flex; align-items:center; gap:8px;">
              <i data-lucide="file-down"></i> Relatório ME — ${this.escapeHtml(r.reportNumber)}
            </div>
            <button type="button" id="btn-close-preview" style="background:transparent; border:none; color:var(--color-graphite-400); cursor:pointer;">✕</button>
          </div>
          <div style="flex:1; position:relative; overflow:hidden;">
            <app-pdf-preview id="me-pdf-preview" style="height:100%; display:block;"></app-pdf-preview>
          </div>
        </div>
      </div>
    `;

    // ClipboardCheck/MessageSquareWarning: ícones de APROVAR RELATÓRIO e
    // DEVOLVER PARA CORREÇÃO. Sem registrá-los aqui o <i data-lucide> fica
    // vazio e os dois botões saem sem ícone, destoando da barra inteira.
    createIcons({ icons: { Save, Eye, CheckCircle, FileDown, Plus, Trash2, Upload, PenLine, Send, ClipboardCheck, MessageSquareWarning }, nameAttr: "data-lucide", root: this });
    bindNdtEngineeringEvents(this, {
      documentType: "ME", report: r, state: this.engineering,
      currentStatus: () => ({ status: r.status, label: this.statusLabel(r.status) }),
      summary: () => this.holdSummary(),
      onChanged: async message => { await this.loadReview(); this.saveNotice = message; this.render(); }
    });
    ndtDeepLinkFocus(this, "me", [["01", "Identificação"], ["02", "Parâmetros"], ["03", "Aparelho / Transdutor"], ["04", "Resultados"], ["05", "Critérios"], ["06", "Croqui"], ["07", "Observações"], ["08", "Signatários"]]);
    if (this.previewOpen && this.previewSnapshot) {
      const preview = this.querySelector("#me-pdf-preview") as any;
      if (preview) preview.snapshot = this.previewSnapshot;
    }
    this.attachEvents(r, readOnly);
  }

  /**
   * "CONTEXTO DO RELATÓRIO": the page always opens here when the route has no
   * report id. Cliente → Projeto → Unidade/Área → Equipamento → Inspeção are
   * chosen here, not through a global session client.
   */
  private renderContextSelection() {
    const canCreate = contextStore.hasPermission(PermissionCode.MeReportCreate);
    const sel = this.selection;
    const inspectionChosen = !!sel.inspectionId;

    const opt = (value: string, label: string, selected: boolean) =>
      `<option value="${this.escapeHtml(value)}" ${selected ? "selected" : ""}>${this.escapeHtml(label)}</option>`;

    this.innerHTML = `
      <style>${this.styles()}</style>

      <div class="me-breadcrumb">
        <span class="crumb-link" id="crumb-reports">Relatórios</span>
        <span>›</span>
        <span>Medição de Espessura (ME)</span>
      </div>

      <div class="me-header">
        <div>
          <h1>Relatório de Inspeção de Medição de Espessura (ME)</h1>
          <p>Preenchimento dos dados técnicos do ensaio e resultados de medição.</p>
        </div>
      </div>

      <section class="me-card me-context-card">
        <div class="card-title"><span class="card-num">00</span> CONTEXTO DO RELATÓRIO</div>
        <p class="legend-note" style="margin-bottom:14px;">Selecione o contexto documental do relatório. O cliente ativo da sessão, quando existir, é apenas uma pré-seleção.</p>
        ${this.renderContextLoadIssues()}

        <div class="card-grid cols-3">
          <label class="field"><span>Cliente *</span>
            <select class="ctl" id="sel-ctx-client">
              ${opt("", this.ctxLoad.emptyLabel("Clientes", "Nenhum Cliente ativo cadastrado", "Selecione o cliente"), !sel.clientId)}
              ${this.clients.map((c: any) => opt(c.id, `${c.code} — ${c.name || c.legalName}`, sel.clientId === c.id)).join("")}
            </select>
          </label>

          <label class="field"><span>Projeto *</span>
            <select class="ctl" id="sel-ctx-project" ${!sel.clientId ? "disabled" : ""}>
              ${opt("", sel.clientId ? this.ctxLoad.emptyLabel("Projetos", "Nenhum Projeto para este Cliente", "Selecione o projeto") : "Selecione o projeto", !sel.projectId)}
              ${this.projects.map((p: any) => opt(p.id, `${p.code} — ${p.name}`, sel.projectId === p.id)).join("")}
            </select>
          </label>

          <label class="field"><span>Unidade / Área *</span>
            <select class="ctl" id="sel-ctx-unit-area" ${!sel.projectId ? "disabled" : ""}>
              ${opt("", sel.projectId ? this.ctxLoad.emptyLabel("Unidades / Áreas", "Nenhuma Unidade/Área para este Projeto", "Selecione a unidade / área") : "Selecione a unidade / área", !sel.unitAreaId)}
              ${this.unitAreas.map((u: any) => opt(u.id, `${u.code} — ${u.name}`, sel.unitAreaId === u.id)).join("")}
            </select>
          </label>

          <label class="field"><span>Equipamento / TAG *</span>
            <select class="ctl" id="sel-ctx-equipment" ${!sel.clientId ? "disabled" : ""}>
              ${opt("", sel.clientId ? this.ctxLoad.emptyLabel("Equipamentos", "Nenhum Equipamento neste contexto", "Selecione o equipamento") : "Selecione o equipamento", !sel.equipmentId)}
              ${this.equipments.map((e: any) => opt(e.id, `${e.tag} — ${e.name || ""} (${EQUIPMENT_TYPE_LABELS[e.equipmentType] || e.equipmentType})`, sel.equipmentId === e.id)).join("")}
            </select>
          </label>

          <label class="field"><span>Ordem de Serviço</span>
            <select class="ctl" id="sel-ctx-workorder" ${!sel.clientId ? "disabled" : ""}>
              ${opt("", sel.clientId ? this.ctxLoad.emptyLabel("Ordens de Serviço", "Nenhuma OS cadastrada para este Cliente", "Selecione a OS") : "Selecione a OS", !sel.workOrderId)}
              ${this.workOrders.map((w: any) => opt(w.id, `${w.code}${w.description ? ` — ${w.description}` : ""}`, sel.workOrderId === w.id)).join("")}
            </select>
          </label>

          <label class="field"><span>Sub-OS</span>
            <select class="ctl" id="sel-ctx-subworkorder" ${!sel.workOrderId ? "disabled" : ""}>
              ${opt("", sel.workOrderId ? this.ctxLoad.emptyLabel("Sub-OS", "Nenhuma Sub-OS para esta OS", "Selecione a Sub-OS") : "Selecione a Sub-OS", !sel.subWorkOrderId)}
              ${this.subWorkOrders.map((s: any) => opt(s.id, `${s.code}${s.description ? ` — ${s.description}` : ""}`, sel.subWorkOrderId === s.id)).join("")}
            </select>
          </label>

          <label class="field" style="grid-column: span 2;"><span>Inspeção (opcional)</span>
            <select class="ctl" id="sel-ctx-inspection" ${!sel.equipmentId ? "disabled" : ""}>
              ${opt("", sel.equipmentId ? this.ctxLoad.emptyLabel("Inspeções", "Nenhuma Inspeção para este Equipamento — pode prosseguir sem", "Selecione a inspeção, se aplicável") : "Selecione a inspeção, se aplicável", !sel.inspectionId)}
              ${this.inspections.map((i: any) => opt(i.id, `${this.formatInspectionLabel(i)}`, sel.inspectionId === i.id)).join("")}
            </select>
            <small class="field-hint">A Inspeção é um vínculo opcional: uma medição avulsa ou pedida pelo Cliente pode existir sem ela, e pode ser vinculada depois.</small>
          </label>
        </div>

        ${this.contextError ? `<div class="me-notice me-notice-error" style="margin-top:14px;">${this.escapeHtml(this.contextError)}</div>` : ""}

        ${inspectionChosen && this.existingReport ? `
          <div class="me-notice" style="margin-top:14px;">
            <strong>RELATÓRIO ME ENCONTRADO</strong><br>
            Nº: ${this.escapeHtml(this.existingReport.reportNumber)} &nbsp;·&nbsp;
            Revisão: Rev. ${this.escapeHtml(this.existingReport.revision)} &nbsp;·&nbsp;
            Status: ${this.statusLabel(this.existingReport.status)}
          </div>
        ` : ""}

        <div class="me-actionbar" style="margin-top:16px; margin-bottom:0;">
          ${inspectionChosen && this.existingReport
            ? `<button class="btn-action primary" id="btn-open-existing">Abrir Relatório</button>`
            : canCreate
              ? `<button class="btn-action primary" id="btn-create-me" ${this.isResolvingContext ? "disabled" : ""}>${this.isResolvingContext ? "Criando..." : "Criar Relatório ME"}</button>`
              : ``}
        </div>
      </section>
    `;

    this.querySelector("#crumb-reports")?.addEventListener("click", () => router.navigate("/reports"));
    this.querySelector("#sel-ctx-client")?.addEventListener("change", (e) => this.onContextChange("clientId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-project")?.addEventListener("change", (e) => this.onContextChange("projectId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-unit-area")?.addEventListener("change", (e) => this.onContextChange("unitAreaId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-equipment")?.addEventListener("change", (e) => this.onContextChange("equipmentId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-inspection")?.addEventListener("change", (e) => this.onContextChange("inspectionId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-workorder")?.addEventListener("change", (e) => this.onContextChange("workOrderId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-subworkorder")?.addEventListener("change", (e) => this.onContextChange("subWorkOrderId", (e.target as HTMLSelectElement).value));
    this.querySelector("#btn-create-me")?.addEventListener("click", () => this.onCreateFromContext());
    this.querySelector("#btn-open-existing")?.addEventListener("click", () => this.onOpenExisting());
  }

  private formatInspectionLabel(inspection: any): string {
    const date = inspection.startDate ? new Date(inspection.startDate).toLocaleDateString("pt-BR") : "sem data";
    const type = inspection.inspectionType || inspection.modality || "Inspeção";
    return `${type} — ${date} — ${inspection.status || ""}`.trim();
  }

  /** The report prints the code alone, so the registered name is shown here as support only. */
  private renderNormHint(r: MeReport): string {
    const selected = this.norms.find(n => n.id === r.referenceNormId);
    if (selected && selected.code && selected.code.trim() && selected.name !== selected.code) {
      return `<small class="field-hint">${this.escapeHtml(selected.name)}</small>`;
    }
    if (!r.referenceNormId && r.referenceNorm) {
      return `<small class="field-hint">Valor do procedimento: ${this.escapeHtml(r.referenceNorm)}</small>`;
    }
    return "";
  }

  /** Inline calibration warning: visible as soon as the equipment is picked, long before "Validar Dados" refuses it. */
  private renderCalibrationHint(assetId: string | null, validity: string): string {
    if (!assetId) return "";
    const status = resolveCalibrationStatus(validity);
    if (status === "EXPIRED") {
      return `<small class="field-hint" style="color: var(--status-danger-solid); font-weight:600;">⚠ CALIBRAÇÃO VENCIDA</small>`;
    }
    if (status === "NO_EXPIRATION_DEFINED") {
      return `<small class="field-hint">Validade não informada no cadastro.</small>`;
    }
    return `<small class="field-hint" style="color: var(--ia-success-fg, #4ade80);">Calibração válida.</small>`;
  }

  private renderValidationBanner(): string {
    if (this.validationOk) {
      return `<div class="me-notice me-notice-ok">Todos os campos obrigatórios estão preenchidos.</div>`;
    }
    const errors = this.validationErrors || [];
    if (errors.length === 0) return "";
    return `<div class="me-notice me-notice-error"><ul>${errors.map(e => `<li>${this.escapeHtml(e)}</li>`).join("")}</ul></div>`;
  }

  /**
   * RASCUNHO → EM ELABORAÇÃO → EM APROVAÇÃO DO PLH → FINALIZADO. O estado
   * "com o PLH" vem do ciclo de revisão, não de um campo paralelo no ME.
   */
  private statusLabel(status: string): string {
    return meWorkflowStatusLabel(status, this.review?.status ?? null);
  }

  /**
   * Cor do badge de situação — segue o RÓTULO, não só `MeReport.status`.
   *
   * O status do módulo continua IN_PROGRESS enquanto o PLH analisa, então
   * usar só ele deixava "EM APROVAÇÃO DO PLH" com a mesma cara de "EM
   * ELABORAÇÃO" — e, pior, `status-in_progress` e `status-finalized` nem
   * existiam no CSS: o badge saía sem cor nenhuma justamente nos dois
   * estados deste fluxo.
   */
  private statusBadgeClass(status: string): string {
    if (this.engineering.hold) return "status-hold";
    const review = this.review?.status ?? null;
    if (review === "CORRECTION_REQUESTED") return "status-correction";
    if (review === "AWAITING_PLH_REVIEW" || review === "PLH_REVIEWING") return "status-plh";
    return `status-${String(status).toLowerCase()}`;
  }

  private renderCardIdentification(r: MeReport, readOnly: boolean): string {
    return `
      <section class="me-card" id="me-sec-01">
        <div class="card-title"><span class="card-num">01</span> IDENTIFICAÇÃO DO RELATÓRIO</div>
        <div class="card-grid cols-3">
          <label class="field"><span>Cliente</span><input class="ctl" value="${this.escapeHtml(r.clientDisplayName)}" readonly title="Vem do Contexto do Relatório — cadastro de Clientes."></label>
          <label class="field"><span>Projeto / Contrato</span><input class="ctl" value="${this.escapeHtml(r.projectContract)}" readonly title="Vem do Contexto do Relatório — cadastro de Projetos."></label>
          <label class="field"><span>Unidade / Área</span><input class="ctl" value="${this.escapeHtml(r.unitArea)}" readonly title="Vem do Contexto do Relatório — cadastro de Unidades/Áreas."></label>

          <label class="field"><span>Equipamento / TAG</span><input class="ctl" value="${this.escapeHtml(r.equipmentTag)}" readonly title="Vem do Contexto do Relatório — cadastro de Equipamentos."></label>
          <label class="field"><span>Tipo</span><input class="ctl" value="${this.escapeHtml(r.equipmentTypeLabel)}" readonly></label>
          <label class="field"><span>Ordem de Serviço</span>
            <select class="ctl" id="sel-report-workorder" ${this.dis(readOnly)} title="Vem do cadastro de Ordens de Serviço — escolhida, nunca digitada. Para corrigir o código, ajuste na origem.">
              <option value="">${this.workOrders.length === 0 ? "Nenhuma OS cadastrada para este contexto" : "Selecione a OS"}</option>
              ${this.workOrders.map((w: any) => `<option value="${this.escapeHtml(w.id)}" ${r.osWorkOrderId === w.id ? "selected" : ""}>${this.escapeHtml(w.code)}${w.description ? ` — ${this.escapeHtml(w.description)}` : ""}</option>`).join("")}
            </select>
            ${r.osNumber ? `<small class="field-hint">Vinculada: ${this.escapeHtml(r.osNumber)}</small>` : ""}
          </label>
          ${this.renderSubOsField(r, readOnly)}

          <label class="field"><span>Data do Ensaio *</span><input class="ctl" type="date" data-field="testDate" value="${this.escapeHtml(r.testDate)}" ${this.dis(readOnly)}></label>
          ${this.renderInspectorField(r, readOnly)}
          <label class="field"><span>Revisão</span><input class="ctl" value="Rev. ${this.escapeHtml(r.revision)}" readonly></label>

          <label class="field"><span>Rel. Cliente Nº</span><input class="ctl" data-field="clientReportNumber" value="${this.escapeHtml(r.clientReportNumber)}" ${this.dis(readOnly)}></label>
          <label class="field"><span>Período (Início)</span><input class="ctl" type="date" data-field="periodStart" value="${this.escapeHtml(r.periodStart)}" ${this.dis(readOnly)}></label>
          <label class="field"><span>Período (Fim)</span><input class="ctl" type="date" data-field="periodEnd" value="${this.escapeHtml(r.periodEnd)}" ${this.dis(readOnly)}></label>
        </div>
      </section>`;
  }

  /**
   * INSPETOR RESPONSÁVEL.
   *
   * Quem não tem `MeReportAssignInspector` (o próprio Inspetor, o Técnico)
   * não escolhe: o campo mostra, já resolvido, o Professional da conta
   * logada. Engenharia e Administração escolhem. A decisão é por PERMISSÃO,
   * nunca por nome de perfil, e o serviço confere a mesma regra.
   */
  private renderInspectorField(r: MeReport, readOnly: boolean): string {
    if (this.canAssignInspector()) {
      return `
        <label class="field"><span>Inspetor Responsável *</span>
          <select class="ctl" id="sel-inspector" ${this.dis(readOnly)} title="Designar o Inspetor exige permissão de Engenharia/Administração.">
            <option value="">Selecione o inspetor</option>
            ${this.professionals.map((p: any) => `<option value="${p.id}" ${r.inspectorProfessionalId === p.id ? "selected" : ""}>${this.escapeHtml(p.name)}</option>`).join("")}
          </select>
          <small class="field-hint">A Sub-OS é filtrada pela responsabilidade deste Inspetor.</small>
        </label>`;
    }

    const auth = contextStore.get();
    const prof = this.professionals.find((p: any) => p.id === r.inspectorProfessionalId || p.userId === auth.userId || p.id === auth.professionalId);
    const inspectorDisplayName = r.inspectorName || prof?.name || auth.userName || "-";
    const missing = !r.inspectorProfessionalId && !auth.userName;
    return `
      <label class="field"><span>Inspetor Responsável *</span>
        <input class="ctl" value="${this.escapeHtml(inspectorDisplayName)}" readonly
               title="Resolvido automaticamente pelo Profissional da sua conta. Designar outro Inspetor exige permissão de Engenharia/Administração.">
        <small class="field-hint">${missing
          ? "Sua conta não tem um Profissional vinculado — peça à Administração para vincular, em Pessoas e Acessos."
          : "Resolvido pela sua conta."}</small>
      </label>`;
  }

  /**
   * SUB-OS — só as que o Inspetor RESPONDE (`SubWorkOrder.professionalId`).
   *
   * Uma única: selecionada sozinha. Várias: escolhe entre as dele. Nenhuma:
   * o motivo é dito, em vez de um select vazio sem explicação.
   */
  private renderSubOsField(r: MeReport, readOnly: boolean): string {
    const resolution = resolveMeSubOs(this.subWorkOrders, r.inspectorProfessionalId, r.osSubWorkOrderId);

    if (resolution.mode === "NO_INSPECTOR" || resolution.mode === "NONE") {
      return `
        <label class="field"><span>Sub-OS</span>
          <input class="ctl" value="" readonly placeholder="—">
          <small class="field-hint field-hint-warn">${this.escapeHtml(resolution.message)}</small>
        </label>`;
    }

    if (resolution.mode === "AUTO") {
      const only = resolution.options[0]!;
      return `
        <label class="field"><span>Sub-OS</span>
          <input class="ctl" value="${this.escapeHtml(`${only.code || ""}${only.description ? ` — ${only.description}` : ""}`)}" readonly
                 title="Única Sub-OS sob responsabilidade deste Inspetor — selecionada automaticamente.">
          <small class="field-hint">Única Sub-OS do Inspetor — vinculada automaticamente.</small>
        </label>`;
    }

    return `
      <label class="field"><span>Sub-OS</span>
        <select class="ctl" id="sel-report-subworkorder" ${this.dis(readOnly || !r.osWorkOrderId)} title="Apenas as Sub-OS sob responsabilidade do Inspetor selecionado.">
          <option value="">Selecione a Sub-OS</option>
          ${resolution.options.map((s: any) => `<option value="${this.escapeHtml(s.id)}" ${resolution.selectedId === s.id ? "selected" : ""}>${this.escapeHtml(s.code)}${s.description ? ` — ${this.escapeHtml(s.description)}` : ""}</option>`).join("")}
        </select>
        <small class="field-hint">${resolution.options.length} Sub-OS sob responsabilidade deste Inspetor.</small>
      </label>`;
  }

  /**
   * O que NÃO pôde ser lido na cascata, e por quê.
   *
   * Sem isto, uma leitura negada por permissão aparecia apenas como um select
   * vazio dizendo "nenhum cadastro" — e o usuário ia procurar um registro que
   * já existia. Um erro de autorização é informação, não ausência de dado.
   */
  private renderContextLoadIssues(): string {
    // Dois avisos distintos: o que não pôde ser LIDO e o que não pôde ser
    // REPOSTO. "A lista de Projetos foi negada" e "o Projeto gravado neste
    // documento não existe mais" mandam procurar coisas diferentes.
    return `<style>${CONTEXT_HYDRATION_STYLES}</style>${this.ctxLoad.banner()}${hydrationBanner(this.hydration)}`;
  }

  private canAssignInspector(): boolean {
    return canAssignInspector([...contextStore.get().permissions]);
  }

  private renderCardParameters(r: MeReport, readOnly: boolean): string {
    const d = this.dis(readOnly);
    return `
      <section class="me-card" id="me-sec-02">
        <div class="card-title"><span class="card-num">02</span> PARÂMETROS DO ENSAIO</div>
        <div class="card-grid cols-3">
          <label class="field" style="grid-column: span 2;"><span>Procedimento / Revisão</span>
            <select class="ctl" id="sel-procedure" ${d}>
              <option value="">${this.procedures.length === 0 ? "Nenhum procedimento de ME cadastrado" : "Selecione o procedimento cadastrado"}</option>
              ${this.procedures.map(p => `<option value="${this.escapeHtml(p.id)}" ${r.procedureId === p.id ? "selected" : ""}>${this.escapeHtml(formatProcedureLabel(p))}${p.status === "INACTIVE" ? " — INATIVO" : ""}</option>`).join("")}
            </select>
            <input class="ctl" data-field="procedureRevision" value="${this.escapeHtml(r.procedureRevision)}" placeholder="Procedimento / revisão" style="margin-top:6px;" ${d}>
          </label>
          <label class="field"><span>Norma de Referência</span>
            <select class="ctl" id="sel-norm" ${d} title="O relatório registra apenas o código cadastrado.">
              <option value="">${this.norms.length === 0 ? "Nenhuma norma cadastrada" : "Selecione..."}</option>
              ${this.norms.map(n => `<option value="${this.escapeHtml(n.id)}" title="${this.escapeHtml(n.name)}" ${r.referenceNormId === n.id ? "selected" : ""}>${this.escapeHtml(formatCatalogItemCode(n))}${n.status === "INACTIVE" ? " — INATIVO" : ""}</option>`).join("")}
            </select>
            ${this.renderNormHint(r)}
          </label>
          <label class="field"><span>Critério de Aceitação</span>
            <select class="ctl" id="sel-criteria" ${d}>
              <option value="">${this.criteria.length === 0 ? "Nenhum critério cadastrado" : "Selecione..."}</option>
              ${this.criteria.map(c => `<option value="${this.escapeHtml(c.id)}" ${r.acceptanceCriteriaId === c.id ? "selected" : ""}>${this.escapeHtml(formatCatalogItemLabel(c))}${c.status === "INACTIVE" ? " — INATIVO" : ""}</option>`).join("")}
            </select>
            ${!r.acceptanceCriteriaId && r.acceptanceCriteria ? `<small class="field-hint">Valor do procedimento: ${this.escapeHtml(r.acceptanceCriteria)}</small>` : ""}
          </label>
          <label class="field"><span>Grupo de Material (aço)</span>
            <searchable-select data-source="material-family" data-field="materialFamilyCode"
              value="${this.escapeHtml(r.materialFamilyCode || "")}"
              placeholder="Digite para pesquisar (ex.: CARBONO, Cr-Mo, DUPLEX)" ${d}></searchable-select>
            ${!r.materialFamilyCode && r.material.trim()
              ? `<small class="field-hint">Registro anterior (texto livre): ${this.escapeHtml(r.material)} — selecione o grupo correspondente.</small>`
              : ""}
          </label>
          <label class="field"><span>Material (especificação)</span>
            <input class="ctl" data-field="materialSpecification" value="${this.escapeHtml(r.materialSpecification || "")}" placeholder="Ex.: ASTM A106 Gr.B (opcional)" ${d}>
          </label>
          <label class="field"><span>Condição Superficial</span>
            <select class="ctl" data-field="surfaceCondition" ${d}>
              <option value="">Selecione...</option>
              ${ME_SURFACE_CONDITIONS.map(s => `<option value="${s}" ${r.surfaceCondition === s ? "selected" : ""}>${s.charAt(0)}${s.slice(1).toLowerCase()}</option>`).join("")}
            </select>
          </label>
          <label class="field"><span>Temperatura da Medição (TME)</span>
            <searchable-select data-source="temperature" data-field="measurementTemperatureC"
              value="${r.measurementTemperatureC !== null && r.measurementTemperatureC !== undefined ? String(r.measurementTemperatureC) : ""}"
              placeholder="0 a ${ME_TEMPERATURE_MAX_C} °C — digite para filtrar" ${d}></searchable-select>
            ${r.measurementTemperatureC === null && r.temperature.trim()
              ? `<small class="field-hint">Registro anterior (texto livre): ${this.escapeHtml(r.temperature)} — selecione a temperatura.</small>`
              : ""}
          </label>
          <label class="field"><span>Método de Correção</span>
            <input class="ctl" value="${this.escapeHtml(METHOD_A_LABEL)}" readonly title="Decisão oficial do módulo: não é escolha do inspetor. Aplicado acima de ${ME_HIGH_TEMPERATURE_THRESHOLD_C} °C.">
            <small class="field-hint">Aplicado automaticamente acima de ${ME_HIGH_TEMPERATURE_THRESHOLD_C} °C, com Vsa do Procedimento vinculado (k=1,0 m/s/°C e Temperatura de Referência=30 °C, fixos).</small>
          </label>
          <label class="field"><span>Nº Certif. Termômetro</span><input class="ctl" data-field="thermometerCert" value="${this.escapeHtml(r.thermometerCert)}" ${d}></label>
          <label class="field"><span>Acoplante</span>
            <select class="ctl" id="sel-couplant" ${d}>
              <option value="">${this.couplants.length === 0 ? "Nenhum acoplante cadastrado" : "Selecione..."}</option>
              ${this.couplants.map(c => `<option value="${this.escapeHtml(c.id)}" ${r.couplantId === c.id ? "selected" : ""}>${this.escapeHtml(formatCatalogItemLabel(c))}${c.status === "INACTIVE" ? " — INATIVO" : ""}</option>`).join("")}
              <option value="${COUPLANT_OTHER}" ${!r.couplantId && r.couplant ? "selected" : ""}>OUTRO</option>
            </select>
            ${!r.couplantId && r.couplant !== undefined && (r.couplant || this.couplantOtherOpen) ? `
              <input class="ctl" data-field="couplant" value="${this.escapeHtml(r.couplant)}" placeholder="Informe o acoplante utilizado" style="margin-top:6px;" ${d}>
            ` : ""}
          </label>
          <label class="field"><span>Bloco de Calibração</span>
            <select class="ctl" id="sel-calibration-block" ${d}>
              <option value="">${this.calibrationBlocks.length === 0 ? "Nenhum bloco cadastrado para este cliente" : "Selecione..."}</option>
              ${this.calibrationBlocks.map(b => `<option value="${this.escapeHtml(b.id)}" ${r.calibrationBlockAssetId === b.id ? "selected" : ""}>${this.escapeHtml([b.internalCode, b.serialNumber].filter(Boolean).join(" / "))}${b.technicalData?.material ? ` — ${this.escapeHtml(b.technicalData.material)}` : ""}</option>`).join("")}
            </select>
            ${r.calibrationBlockMaterial || r.calibrationBlockRange ? `<small class="field-hint">${[r.calibrationBlockMaterial ? `Material: ${this.escapeHtml(r.calibrationBlockMaterial)}` : "", r.calibrationBlockRange ? `Faixa: ${this.escapeHtml(r.calibrationBlockRange)}` : ""].filter(Boolean).join(" · ")}</small>` : ""}
          </label>
          <label class="field"><span>Nº Cert. Calibração Bloco</span>
            <input class="ctl" value="${this.escapeHtml(r.calibrationBlockCert || (r.calibrationBlockAssetId ? "NÃO INFORMADO" : ""))}" readonly title="Preenchido a partir do certificado registrado no Cadastro de Equipamentos de END.">
            ${this.renderCalibrationHint(r.calibrationBlockAssetId, r.calibrationBlockCertValidity)}
          </label>
          <label class="field"><span>Técnica de Medição</span>
            <select class="ctl" id="sel-technique" ${d}>
              <option value="">Selecione a técnica</option>
              ${ME_MEASUREMENT_TECHNIQUES.map(t => `<option value="${this.escapeHtml(t)}" ${r.measurementTechnique === t ? "selected" : ""}>${this.escapeHtml(t)}</option>`).join("")}
            </select>
            ${isOtherTechnique(r.measurementTechnique) ? `
              <input class="ctl" data-field="measurementTechniqueOther" value="${this.escapeHtml(r.measurementTechniqueOther)}" placeholder="Descreva a técnica aplicada" style="margin-top:6px;" ${d}>
            ` : ""}
          </label>
          <label class="field"><span>Fator de Correção (FC_A)</span>
            <input class="ctl" id="me-fc-summary" value="${this.escapeHtml(this.factorSummary(r))}" readonly title="FC_A = (Vsa − k×ΔT) ÷ Vsa, do Procedimento vinculado a cada ponto. Nunca é digitado.">
            ${r.correctionFactor && r.correctionFactor.trim() ? `<small class="field-hint">Registro anterior (digitado): ${this.escapeHtml(r.correctionFactor)} — não é usado no cálculo.</small>` : ""}
          </label>
        </div>
        ${this.renderHighTemperaturePanel(r)}
      </section>`;
  }

  /**
   * Painel de MEDIÇÃO EM ALTA TEMPERATURA — aparece só acima de 60 °C.
   *
   * MÉTODO A — RESULTADO APROXIMADO, somente leitura: o método não é escolha
   * do inspetor. Mostra o que o sistema resolveu (Vsa do Procedimento
   * vinculado; k e Temperatura de Referência são as constantes fixas do
   * Método A — FC_A) e, quando falta dado, DIZ POR QUÊ — para a pessoa saber
   * o que corrigir. Nunca estima fator: sem Vsa a Ea fica vazia (pendência
   * explícita).
   */
  private renderHighTemperaturePanel(r: MeReport): string {
    if (!isMeHighTemperature(r)) return `<div id="me-ht-panel"></div>`;

    const summary = r.thermalCorrection;
    const points = Array.isArray(r.points) ? r.points : [];
    const corrected = points.filter(p => p.hotCorrection?.status === "CORRECTED");
    const material = [r.materialFamilyLabel || materialFamilyLabel(r.materialFamilyCode), r.materialSpecification].filter(Boolean).join(" — ") || "não informado";
    const legend = ME_HIGH_TEMPERATURE_LEGEND.map(line => this.escapeHtml(line)).join(" · ");
    const status = summary?.status ?? "INCOMPLETE";
    const procedureCode = summary?.procedureCode || corrected.find(p => p.hotCorrection?.procedureCode)?.hotCorrection?.procedureCode || "";

    const STATUS_TEXT: Record<string, string> = {
      CORRECTED: "● MÉTODO A — RESULTADO APROXIMADO APLICADO",
      INCOMPLETE: "● AGUARDANDO DADOS PARA A CORREÇÃO"
    };
    const tone = status === "CORRECTED" ? "ht-ok" : "ht-warn";
    const partial = status !== "CORRECTED" && corrected.length > 0
      ? `<p class="ht-line">${corrected.length} de ${points.length} ponto(s) corrigido(s). Os demais ficam com Ea vazia até a pendência ser resolvida.</p>`
      : "";

    return `
      <div id="me-ht-panel" class="ht-panel ${tone}">
        <div class="ht-head">MEDIÇÃO EM ALTA TEMPERATURA — TME acima de ${ME_HIGH_TEMPERATURE_THRESHOLD_C} °C</div>
        <div class="ht-grid">
          <div><span>Material</span><strong>${this.escapeHtml(material)}</strong></div>
          <div><span>Temperatura (TME)</span><strong>${r.measurementTemperatureC ?? "—"} °C</strong></div>
          <div><span>Método</span><strong>${this.escapeHtml(METHOD_A_LABEL)}</strong></div>
          <div><span>Procedimento</span><strong>${procedureCode ? this.escapeHtml(procedureCode) : "—"}</strong></div>
          <div><span>Fator de correção (FC_A)</span><strong>${this.escapeHtml(this.factorSummary(r))}</strong></div>
        </div>
        <p class="ht-title">${STATUS_TEXT[status] ?? this.escapeHtml(status)}</p>
        ${status === "CORRECTED"
          ? `<p class="ht-line">A coluna <strong>Ea</strong> (Emq × FC_A) é a espessura APROXIMADA usada no laudo, nos cálculos e na AVR. A <strong>Emq</strong> permanece registrada.</p>`
          : `<p class="ht-line">${this.escapeHtml(summary?.message || "Informe Emq, grupo de material, procedimento (com Vsa), aparelho, transdutor e acoplante.")}</p>`}
        ${partial}
        <p class="ht-legend">${legend}</p>
      </div>`;
  }

  /** FC do relatório: um só, vários por ponto, ou nenhum — nunca um número presumido. */
  private factorSummary(r: MeReport): string {
    if (!isMeHighTemperature(r)) return `Não aplicável (TME até ${ME_HIGH_TEMPERATURE_THRESHOLD_C} °C)`;
    const factors = [...new Set((r.points || [])
      .map(p => p.hotCorrection)
      .filter(h => h?.status === "CORRECTED" && typeof h.correctionFactor === "number")
      .map(h => formatFactor(h!.correctionFactor as number)))];
    if (factors.length === 0) return "Não calculado — sem calibração válida";
    return factors.length === 1 ? factors[0]! : `Por ponto (${factors.join(" · ")})`;
  }

  private renderCardEquipment(r: MeReport, readOnly: boolean): string {
    const d = this.dis(readOnly);
    return `
      <section class="me-card" id="me-sec-03">
        <div class="card-title"><span class="card-num">03</span> APARELHO / TRANSDUTOR</div>
        <div class="equip-cols">
          <div class="equip-box">
            <div class="equip-box-title">APARELHO DE ME-US</div>
            <label class="field"><span>Aparelho cadastrado</span>
              <select class="ctl" id="sel-device-asset" ${d}>
                <option value="">${this.devices.length === 0 ? "Nenhum aparelho cadastrado para este cliente" : "Selecione..."}</option>
                ${this.devices.map(a => `<option value="${this.escapeHtml(a.id)}" ${r.deviceAssetId === a.id ? "selected" : ""}>${this.escapeHtml([a.internalCode, a.manufacturer, a.model].filter(Boolean).join(" — "))}</option>`).join("")}
              </select>
            </label>
            <label class="field"><span>Fabricante</span><input class="ctl" value="${this.escapeHtml(r.deviceManufacturer)}" readonly title="Editar no Cadastro de Equipamentos de END."></label>
            <label class="field"><span>Modelo</span><input class="ctl" value="${this.escapeHtml(r.deviceModel)}" readonly title="Editar no Cadastro de Equipamentos de END."></label>
            <label class="field"><span>Nº de Série</span><input class="ctl" value="${this.escapeHtml(r.deviceSerialNumber)}" readonly></label>
            <label class="field"><span>Certif. Calibração</span><input class="ctl" value="${this.escapeHtml(r.deviceCalibrationCert || (r.deviceAssetId ? "NÃO INFORMADO" : ""))}" readonly></label>
            <label class="field"><span>Validade</span>
              <input class="ctl" value="${this.escapeHtml(r.deviceCalibrationValidity || (r.deviceAssetId ? "NÃO INFORMADO" : ""))}" readonly>
              ${this.renderCalibrationHint(r.deviceAssetId, r.deviceCalibrationValidity)}
            </label>
          </div>
          <div class="equip-box">
            <div class="equip-box-title">TRANSDUTOR</div>
            <label class="field"><span>Transdutor cadastrado</span>
              <select class="ctl" id="sel-transducer-asset" ${d}>
                <option value="">${this.transducers.length === 0 ? "Nenhum transdutor cadastrado para este cliente" : "Selecione..."}</option>
                ${this.transducers.map(a => `<option value="${this.escapeHtml(a.id)}" ${r.transducerAssetId === a.id ? "selected" : ""}>${this.escapeHtml([a.internalCode, a.manufacturer, a.model].filter(Boolean).join(" — "))}</option>`).join("")}
              </select>
            </label>
            <label class="field"><span>Fabricante</span><input class="ctl" value="${this.escapeHtml(r.transducerManufacturer)}" readonly title="Editar no Cadastro de Equipamentos de END."></label>
            <label class="field"><span>Modelo</span><input class="ctl" value="${this.escapeHtml(r.transducerModel)}" readonly title="Editar no Cadastro de Equipamentos de END."></label>
            <label class="field"><span>Tipo do Transdutor</span><input class="ctl" value="${this.escapeHtml(r.transducerType)}" readonly title="Editar no Cadastro de Equipamentos de END."></label>
            <label class="field"><span>Frequência</span><input class="ctl" value="${this.escapeHtml(r.transducerFrequency)}" readonly></label>
            <label class="field"><span>Dimensão</span><input class="ctl" value="${this.escapeHtml(r.transducerDimension)}" readonly></label>
            <label class="field"><span>Identificação</span><input class="ctl" value="${this.escapeHtml(r.transducerIdentification)}" readonly></label>
            <label class="field"><span>Nº de Série</span><input class="ctl" value="${this.escapeHtml(r.transducerSerialNumber)}" readonly></label>
          </div>
        </div>
      </section>`;
  }

  private renderCardResults(r: MeReport, readOnly: boolean): string {
    const d = this.dis(readOnly);
    // Em alta temperatura a coluna medida É a Emq (Espessura Medida a
    // Quente), e ganham espaço a TME e a Ea (Espessura Aproximada, MÉTODO
    // A). No fluxo convencional as três colunas extras nem existem — não
    // adianta carregar a tabela com cabeçalho que nunca terá valor.
    const highTemperature = isMeHighTemperature(r);
    const measuredHeader = highTemperature ? "Emq (mm)" : "ESP. ATUAL (mm)";
    const columns = highTemperature ? 12 : 8;

    return `
      <section class="me-card" id="me-sec-04">
        <div class="card-title-row">
          <div class="card-title"><span class="card-num">04</span> RESULTADOS DAS MEDIÇÕES</div>
          ${!readOnly ? `<button class="btn-action sm" id="btn-add-point"><i data-lucide="plus"></i> Adicionar Ponto</button>` : ""}
        </div>
        <div class="me-table-wrap">
          <table class="me-table">
            <thead><tr>
              <th>ID DO PONTO</th><th>COMPONENTE</th><th>LOCALIZAÇÃO</th>
              <th>ESP. NOM. (mm)</th><th>ESP. MÍN. (mm)</th><th>${measuredHeader}</th>
              ${highTemperature ? `<th>TME (°C)</th><th>FC_A</th><th>Ea (mm)</th><th>PROCEDIMENTO</th>` : ""}
              <th>LAUDO</th><th></th>
            </tr></thead>
            <tbody>
              ${r.points.length === 0 ? `<tr><td colspan="${columns}" class="me-table-empty">Nenhum ponto de medição adicionado.</td></tr>` : ""}
              ${r.points.map(p => `
                <tr>
                  <td><input class="ctl-cell pt-id" data-point-id="${p.key}" data-point-field="id" value="${this.escapeHtml(p.id)}" title="Identificação do ponto conforme o croqui — edite livremente." ${d}></td>
                  <td><input class="ctl-cell" data-point-id="${p.key}" data-point-field="component" value="${this.escapeHtml(p.component)}" ${d}></td>
                  <td><input class="ctl-cell" data-point-id="${p.key}" data-point-field="location" value="${this.escapeHtml(p.location)}" ${d}></td>
                  <td><input class="ctl-cell num" data-point-id="${p.key}" data-point-field="nominalThickness" value="${this.escapeHtml(formatDecimalBR(p.nominalThickness))}" inputmode="decimal" placeholder="0,00" ${d}></td>
                  <td><input class="ctl-cell num" data-point-id="${p.key}" data-point-field="minThickness" value="${this.escapeHtml(formatDecimalBR(p.minThickness))}" inputmode="decimal" placeholder="0,00" ${d}></td>
                  <td><input class="ctl-cell num" data-point-id="${p.key}" data-point-field="actualThickness" value="${this.escapeHtml(formatDecimalBR(p.actualThickness))}" inputmode="decimal" placeholder="0,00" ${d}></td>
                  ${highTemperature ? `
                    <td class="me-cell-derived">${r.measurementTemperatureC ?? ""}</td>
                    <td class="me-cell-derived" data-fc-for="${p.key}">${this.renderFcCell(p)}</td>
                    <td class="me-cell-derived" data-ect-for="${p.key}">${this.renderEctCell(p)}</td>
                    <td class="me-cell-cal" data-cal-for="${p.key}">${this.renderCalibrationCell(p)}</td>
                  ` : ""}
                  <td data-laudo-for="${p.key}">${this.renderLaudoCell(r, p, readOnly)}</td>
                  <td>${!readOnly ? `<button class="btn-icon-danger" data-remove-point="${p.key}" title="Excluir ponto"><i data-lucide="trash-2"></i></button>` : ""}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>
        ${highTemperature ? `<p class="ht-legend">${ME_HIGH_TEMPERATURE_LEGEND.map(l => this.escapeHtml(l)).join(" · ")}</p>` : ""}
      </section>`;
  }

  /** FC_A do ponto (precisão total), do Procedimento vinculado. Somente leitura — nunca digitado. */
  private renderFcCell(point: MeResultPoint): string {
    const hot = point.hotCorrection;
    if (hot?.status === "CORRECTED" && typeof hot.correctionFactor === "number") {
      const title = `FC_A = (Vsa − k×ΔT) ÷ Vsa = (${formatDecimalBR(hot.referenceVelocityMs)} − ${formatDecimalBR(hot.coefficientK)}×${formatDecimalBR(hot.deltaTemperatureC)}) ÷ ${formatDecimalBR(hot.referenceVelocityMs)} (${hot.procedureCode})`;
      return `<span title="${this.escapeHtml(title)}">${this.escapeHtml(formatFactor(hot.correctionFactor))}</span>`;
    }
    return `<span class="ect-missing" title="${this.escapeHtml(hot?.message || "FC_A não calculado.")}">—</span>`;
  }

  /**
   * Ea (espessura APROXIMADA) do ponto. Sem Vsa do Procedimento vinculado, a
   * célula diz "—" com o motivo no title, em vez de repetir a Emq: repetir
   * faria parecer que a correção foi feita e resultou no mesmo número.
   */
  private renderEctCell(point: MeResultPoint): string {
    if (typeof point.correctedThickness === "number" && Number.isFinite(point.correctedThickness)) {
      return this.escapeHtml(formatDecimalBR(point.correctedThickness));
    }
    return `<span class="ect-missing" title="${this.escapeHtml(point.hotCorrection?.message || "Ea não calculada: falta Vsa do Procedimento (MÉTODO A) para esta medição.")}">—</span>`;
  }

  /** Indicador da linha: ✓ MÉTODO A — APROXIMADO, ou a pendência, com o motivo no title. */
  private renderCalibrationCell(point: MeResultPoint): string {
    const hot = point.hotCorrection;
    if (!hot) return `<span class="hot-sub">—</span>`;
    const title = this.escapeHtml(hot.message);
    switch (hot.status) {
      case "CORRECTED":
        return `<span class="hot-ok">✓ MÉTODO A — APROXIMADO</span><span class="hot-sub">${this.escapeHtml(hot.procedureCode)}</span>`;
      case "INCOMPLETE":
        return `<span class="hot-warn" title="${title}">⚠ DADOS PENDENTES</span>`;
      default:
        return `<span class="hot-sub" title="${title}">Aguardando dados</span>`;
    }
  }

  /**
   * Recalcula e atualiza FC_A/Ea/PROCEDIMENTO/LAUDO de todos os pontos, o
   * painel e o FC_A do ITEM 2 — sem re-renderizar a tabela (perderia o foco).
   */
  private refreshHotCells(): void {
    if (!this.report) return;
    this.recomputeHot();
    const r = this.report;
    for (const p of r.points) {
      const set = (attr: string, html: string) => {
        const cell = this.querySelector(`[${attr}="${p.key}"]`);
        if (cell) cell.innerHTML = html;
      };
      set("data-fc-for", this.renderFcCell(p));
      set("data-ect-for", this.renderEctCell(p));
      set("data-cal-for", this.renderCalibrationCell(p));
      set("data-laudo-for", this.renderLaudoCell(r, p));
    }
    const panel = this.querySelector("#me-ht-panel");
    if (panel) panel.outerHTML = this.renderHighTemperaturePanel(r);
    const fc = this.querySelector("#me-fc-summary") as HTMLInputElement | null;
    if (fc) fc.value = this.factorSummary(r);
  }

  /**
   * Laudo is derived, never typed: symbol + code so it never depends on
   * colour alone. Below the minimum the calculated R may be moved to ADPC
   * (a formal client decision) — and to nothing else.
   */
  private renderLaudoCell(report: MeReport, point: MeResultPoint, readOnly = false): string {
    // Em alta temperatura o laudo julga a Ea (Espessura Aproximada, MÉTODO
    // A) — não a leitura feita quente (Emq).
    const effective = meEffectiveThickness(report, point);
    const calculated = resolveLaudo(point.minThickness, effective);
    const final = resolveFinalLaudo(point.minThickness, effective, point.laudoOverride as any);
    const options = allowedLaudoOverrides(calculated);

    const badge = (code: typeof final) => {
      const cls = code === "A" ? "laudo-approved" : code === "R" ? "laudo-rejected" : code === "ADPC" ? "laudo-adpc" : "laudo-pending";
      // Full label, not just the code: the result must be readable in the
      // table without consulting the legend.
      return `<span class="laudo-badge ${cls}" title="${this.escapeHtml(ME_LAUDO_LABELS[code])}">${ME_LAUDO_SYMBOLS[code]} ${this.escapeHtml(ME_LAUDO_LABELS[code])}</span>`;
    };

    if (options.length < 2 || readOnly) {
      return badge(final);
    }

    return `
      ${badge(final)}
      <select class="ctl-cell laudo-select" data-point-id="${point.key}" data-laudo-decision="1" title="Calculado: ${this.escapeHtml(ME_LAUDO_LABELS[calculated])}">
        ${options.map(o => `<option value="${o}" ${final === o ? "selected" : ""}>${this.escapeHtml(ME_LAUDO_LABELS[o])}</option>`).join("")}
      </select>
      ${point.laudoOverride === "ADPC" ? `<small class="field-hint">Calculado: R — override registrado</small>` : ""}
    `;
  }

  /** Records an explicit laudo decision (only the permitted ones) and keeps it traceable. */
  private onLaudoDecision(pointKey: string, chosen: string) {
    if (!this.report) return;
    const p = this.report.points.find(pt => pt.key === pointKey);
    if (!p) return;

    // Em alta temperatura o laudo calculado julga a Ea, como na célula.
    const calculated = resolveLaudo(p.minThickness, meEffectiveThickness(this.report, p));
    if (!isLaudoOverrideAllowed(calculated, chosen as any)) return;

    if (chosen === calculated) {
      delete (p as any).laudoOverride;
      delete (p as any).laudoOverrideReason;
    } else {
      (p as any).laudoOverride = chosen;
      const reason = window.prompt(
        "Justificativa para encaminhar este ponto como ADPC (A SER DEFINIDO PELO CLIENTE):",
        p.laudoOverrideReason || "Resultado encaminhado para definição do Cliente."
      );
      (p as any).laudoOverrideReason = (reason || "").trim() || "Resultado encaminhado para definição do Cliente.";
    }

    this.scheduleAutosave();
    this.render();
  }

  /**
   * Updates just the laudo cell while the user types, without re-rendering the
   * row (which would drop focus).
   *
   * Em alta temperatura, recalcula antes a correção térmica no cliente com a
   * MESMA função do serviço — assim a Ea e o laudo acompanham a digitação da
   * Emq em vez de ficarem congelados no último valor gravado. A gravação
   * refaz o cálculo no servidor, que continua sendo a versão que vale.
   */
  private refreshLaudoCell(point: MeResultPoint) {
    if (!this.report) return;

    if (isMeHighTemperature(this.report)) {
      // Alterou a Emq: recalcula o MÉTODO A e atualiza FC_A/Ea/procedimento
      // de TODOS os pontos.
      this.refreshHotCells();
      return;
    }

    const cell = this.querySelector(`[data-laudo-for="${point.key}"]`);
    if (cell) cell.innerHTML = this.renderLaudoCell(this.report, point);
  }

  private renderCardLegend(): string {
    return `
      <section class="me-card" id="me-sec-05">
        <div class="card-title"><span class="card-num">05</span> LEGENDA E CRITÉRIOS DE RESULTADO</div>
        <div class="legend-text">
          <strong>(A)</strong> Aprovado &nbsp;|&nbsp; <strong>(R)</strong> Reprovado &nbsp;|&nbsp; <strong>(EC)</strong> Exame Complementar &nbsp;|&nbsp; <strong>(P)</strong> Em Elaboração &nbsp;|&nbsp; <strong>(ADPC)</strong> A Ser Definido pelo Cliente
        </div>
        <p class="legend-note">O enquadramento final deverá refletir o critério técnico aplicável e os dados efetivamente medidos.</p>
      </section>`;
  }

  private renderCardCroqui(r: MeReport, readOnly: boolean): string {
    return `
      <section class="me-card" id="me-sec-06">
        <div class="card-title"><span class="card-num">06</span> CROQUI / ESQUEMA DE LOCALIZAÇÃO</div>
        ${r.croquiImage ? `
          <div class="croqui-preview">
            <img src="${r.croquiImage}" alt="Croqui" />
            ${!readOnly ? `<div class="croqui-actions">
              <button class="btn-action sm" id="btn-croqui-replace">Substituir</button>
              <button class="btn-action sm danger" id="btn-croqui-remove">Remover</button>
            </div>` : ""}
          </div>
        ` : `
          <label class="croqui-dropzone" for="inp-croqui">
            <i data-lucide="upload"></i>
            <span>Arraste uma imagem aqui ou clique para selecionar</span>
            <small>Formatos aceitos: PNG, JPG, PDF (máx. 10 MB)</small>
          </label>
        `}
        <input type="file" id="inp-croqui" accept="image/png,image/jpeg,application/pdf" style="display:none;" ${readOnly ? "disabled" : ""}>
      </section>`;
  }

  private renderCardObservations(r: MeReport, readOnly: boolean): string {
    return `
      <section class="me-card" id="me-sec-07">
        <div class="card-title"><span class="card-num">07</span> COMENTÁRIOS E OBSERVAÇÕES</div>
        <textarea class="ctl textarea" data-field="comments" placeholder="Digite aqui os comentários e observações..." ${this.dis(readOnly)}>${this.escapeHtml(r.comments)}</textarea>
        <p class="legend-note">Campo opcional — não bloqueia a emissão do relatório quando vazio.</p>
      </section>`;
  }

  private renderCardSignatories(r: MeReport, readOnly: boolean, canSign: boolean): string {
    const insp = r.signatories.find(s => s.role === "INSPETOR");
    const plh = r.signatories.find(s => s.role === "PLH");
    const fisc = r.signatories.find(s => s.role === "FISCALIZACAO") || { name: "", functionTitle: "", credential: "" } as any;

    return `
      <section class="me-card" id="me-sec-08">
        <div class="card-title"><span class="card-num">08</span> TERMO DE RESPONSABILIDADE & SIGNATÁRIOS</div>
        <p class="legend-note">Responsabilizam-se pelas informações aqui contidas, para os devidos efeitos, os signatários abaixo:</p>

        <div class="sig-row">
          <div class="sig-lbl">Inspetor Responsável</div>
          <div class="sig-val">${this.escapeHtml(insp?.name || "-")}<br><small>${this.escapeHtml(insp?.functionTitle || "")} · ${this.escapeHtml(insp?.credential || "sem credencial")}</small></div>
          <div class="sig-status">${insp?.signatureImageId ? "✔ assinatura cadastrada" : "sem assinatura cadastrada"}</div>
        </div>

        <div class="sig-row">
          <div class="sig-lbl">PLH / Aprovador</div>
          <div class="sig-val">
            ${plh?.name
              ? `${this.escapeHtml(plh.name)}<br><small>${this.escapeHtml(plh.functionTitle || "ENGENHEIRO PLH")} · ${this.escapeHtml(plh.credential || "sem credencial")}</small>`
              : `<span class="sig-unresolved">Este Cliente ainda não tem Engenheiro PLH responsável definido.</span><br><small>Defina em Administração → Clientes. O PLH não é escolhido no relatório.</small>`}
          </div>
          <div class="sig-status">${plh?.signatureImageId ? "✔ assinatura cadastrada" : "sem assinatura cadastrada"}</div>
        </div>
        ${canSign ? `
          <div class="sig-row">
            <div></div>
            <div>
              ${r.status === "SIGNED"
                ? `<span class="status-badge status-signed">Assinado em ${new Date(r.signedAt || "").toLocaleString("pt-BR")}</span>`
                : `<button class="btn-action sm" id="btn-sign-plh"><i data-lucide="pen-line"></i> Assinar como PLH</button>`}
            </div>
            <div></div>
          </div>` : ""}

        <div class="sig-row">
          <div class="sig-lbl">Fiscalização</div>
          <div class="sig-val">
            <input class="ctl" data-sig-role="FISCALIZACAO" data-sig-field="name" placeholder="Nome do fiscal" value="${this.escapeHtml(fisc.name)}" ${this.dis(readOnly)}>
            <input class="ctl" data-sig-role="FISCALIZACAO" data-sig-field="credential" placeholder="Matrícula / Identificação" value="${this.escapeHtml(fisc.credential)}" style="margin-top:6px;" ${this.dis(readOnly)}>
          </div>
          <div class="sig-status">opcional</div>
        </div>
      </section>`;
  }

  private attachEvents(r: MeReport, readOnly: boolean) {
    this.querySelector("#crumb-reports")?.addEventListener("click", () => router.navigate("/reports"));
    this.querySelector("#crumb-thickness")?.addEventListener("click", () => router.navigate("/ndt/thickness"));

    this.querySelector("#btn-save-draft")?.addEventListener("click", () => this.onSaveDraft());
    this.querySelector("#btn-preview")?.addEventListener("click", () => this.onPreviewOrPdf());
    this.querySelector("#btn-generate-pdf")?.addEventListener("click", () => this.onPreviewOrPdf());
    this.querySelector("#btn-validate")?.addEventListener("click", () => this.onValidate());
    this.querySelector("#btn-sign-plh")?.addEventListener("click", () => this.onSignAsPlh());
    this.querySelector("#btn-submit-to-plh")?.addEventListener("click", () => void this.onSubmitToPlh());
    this.querySelector("#btn-release-cancel")?.addEventListener("click", () => { this.showReleaseConfirm = false; this.render(); });
    this.querySelector("#btn-release-confirm")?.addEventListener("click", () => void this.confirmReleaseToPlh());
    this.querySelector("#btn-plh-start")?.addEventListener("click", () => void this.onStartPlhAnalysis());
    this.querySelector("#btn-plh-approve")?.addEventListener("click", () => { this.showApproveConfirm = true; this.approveFailure = null; this.render(); });
    this.querySelector("#btn-plh-approve-cancel")?.addEventListener("click", () => { this.showApproveConfirm = false; this.render(); });
    this.querySelector("#btn-plh-approve-confirm")?.addEventListener("click", () => void this.confirmApproveReport());
    this.querySelector("#btn-plh-return")?.addEventListener("click", () => this.returnForCorrection());
    this.querySelector("#btn-release-back")?.addEventListener("click", () => { this.releasePendencies = null; this.render(); });
    // Deep link da pendência: fecha o aviso e rola até o item incompleto.
    this.querySelectorAll("[data-goto-item]").forEach(button => button.addEventListener("click", () => {
      const item = (button as HTMLElement).dataset["gotoItem"];
      this.releasePendencies = null;
      this.render();
      this.querySelector(`#me-sec-0${item}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }));
    this.querySelector("#btn-close-preview")?.addEventListener("click", () => this.closePreview());

    this.querySelector("#btn-change-context")?.addEventListener("click", () => this.onChangeContext());
    this.querySelector("#sel-report-workorder")?.addEventListener("change", (e) => this.onReportWorkOrderChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-report-subworkorder")?.addEventListener("change", (e) => this.onReportSubWorkOrderChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-procedure")?.addEventListener("change", (e) => this.onProcedureChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-technique")?.addEventListener("change", (e) => this.onTechniqueChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-norm")?.addEventListener("change", (e) => this.onCatalogSelection("referenceNorm", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-criteria")?.addEventListener("change", (e) => this.onCatalogSelection("acceptanceCriteria", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-couplant")?.addEventListener("change", (e) => this.onCatalogSelection("couplant", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-calibration-block")?.addEventListener("change", (e) => this.onCalibrationBlockChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-device-asset")?.addEventListener("change", (e) => this.onDeviceAssetChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-transducer-asset")?.addEventListener("change", (e) => this.onTransducerAssetChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-inspector")?.addEventListener("change", (e) => this.onInspectorChange((e.target as HTMLSelectElement).value));

    if (!readOnly) {
      this.querySelector("#btn-add-point")?.addEventListener("click", () => this.addPoint());
      this.querySelectorAll("[data-remove-point]").forEach(btn => {
        btn.addEventListener("click", () => this.removePoint((btn as HTMLElement).dataset["removePoint"]!));
      });

      const fileInput = this.querySelector("#inp-croqui") as HTMLInputElement | null;
      this.querySelector(".croqui-dropzone")?.addEventListener("click", (e) => {
        e.preventDefault();
        fileInput?.click();
      });
      this.querySelector("#btn-croqui-replace")?.addEventListener("click", () => fileInput?.click());
      this.querySelector("#btn-croqui-remove")?.addEventListener("click", () => {
        if (this.report) this.report.croquiImage = null;
        this.scheduleAutosave();
        this.render();
      });
      fileInput?.addEventListener("change", () => {
        const file = fileInput.files?.[0];
        if (file) this.onCroquiFile(file);
      });
    }

    this.querySelectorAll(".me-nav-item").forEach(a => {
      a.addEventListener("click", (e) => {
        e.preventDefault();
        const sec = (a as HTMLElement).dataset["sec"];
        this.querySelector(`#me-sec-${sec}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }

  private dis(readOnly: boolean): string {
    return readOnly ? "disabled" : "";
  }

  private escapeHtml(str: string | null | undefined): string {
    return (str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private styles(): string {
    return `
      :host, app-me-report-page { display: block; }
      ${PLH_REVIEW_UI_STYLES}
      ${HOLD_UI_STYLES}
      ${CORRECTION_UI_STYLES}

      .me-empty-state { display:flex; flex-direction:column; align-items:center; gap:10px; text-align:center; padding:64px 24px; color: var(--color-text-disabled); }
      .me-empty-state h2 { font-size:1.1rem; color: var(--color-text-primary); margin:0 0 4px 0; }
      .me-empty-state p { margin:0; font-size:0.88rem; }
      .me-empty-state .me-empty-hint { color: var(--color-text-disabled); font-size:0.8rem; max-width:420px; }
      .me-empty-state .btn-action { margin-top:10px; }
      .me-context-card { max-width: 1100px; }
      .me-breadcrumb { display:flex; gap:8px; align-items:center; font-size:0.8rem; color: var(--color-text-disabled); margin-bottom: 12px; }
      .me-breadcrumb .crumb-link { cursor:pointer; color: var(--color-text-muted, #AEAEAE); }
      .me-breadcrumb .crumb-link:hover { text-decoration: underline; }

      .me-header { display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:16px; margin-bottom:16px; }
      .me-header h1 { font-size:1.35rem; font-weight:600; color: var(--color-text-primary); margin:0 0 4px 0; }
      .me-header p { margin:0; font-size:0.85rem; color: var(--color-text-disabled); }
      .me-header-meta { display:flex; gap:18px; flex-wrap:wrap; }
      .meta-item { display:flex; flex-direction:column; gap:2px; }
      .meta-lbl { font-size:0.65rem; text-transform:uppercase; color: var(--color-text-disabled); }
      .meta-val { font-size:0.9rem; color: var(--color-text-primary); font-weight:600; }

      .status-badge { display:inline-block; padding:2px 10px; border-radius:12px; font-size:0.7rem; font-weight:700; }
      .status-draft { background: rgba(250,204,21,0.15); color: var(--ia-warning-fg, #facc15); }
      .status-in_progress { background: rgba(148,163,184,0.18); color: var(--ia-fg, #cbd5e1); }
      .status-validated { background: rgba(96,165,250,0.15); color: var(--ia-info-fg, #60a5fa); }
      /* Em análise do PLH — mesmo roxo do banner de revisão. */
      .status-plh { background: rgba(124,58,237,0.18); color: var(--ia-fg, #ddd6fe); }
      .status-correction { background: rgba(245,158,11,0.18); color: var(--ia-warning-fg, #fde68a); }
      .status-finalized { background: rgba(16,185,129,0.18); color: var(--ia-success-fg, #a7f3d0); }
      .status-hold { background: rgba(239,68,68,0.18); color: var(--ia-danger-fg, #fca5a5); }
      .status-signed { background: rgba(74,222,128,0.15); color: var(--ia-success-fg, #4ade80); }

      /* MEDIÇÃO EM ALTA TEMPERATURA — painel e colunas derivadas. */
      .ht-panel { margin-top:12px; padding:10px 12px; border-radius:6px; border:1px solid; font-size:0.78rem; }
      .ht-warn { border-color: rgba(245,158,11,0.45); background: rgba(245,158,11,0.10); color: var(--ia-warning-fg, #fde68a); }
      .ht-ok { border-color: rgba(16,185,129,0.40); background: rgba(16,185,129,0.10); color: var(--ia-success-fg, #a7f3d0); }
      .ht-head { font-weight:700; font-size:0.72rem; letter-spacing:0.04em; text-transform:uppercase; margin-bottom:6px; }
      .ht-title { font-weight:700; margin:0 0 4px 0; }
      .ht-line { margin:0 0 4px 0; line-height:1.45; }
      .ht-legend { margin:8px 0 0 0; font-size:0.68rem; color: var(--ia-fg-soft, #AEAEAE); }
      .me-cell-derived { text-align:center; font-variant-numeric: tabular-nums; }
      .ect-missing { color: var(--ia-warning-fg, #fbbf24); font-weight:700; }
      .me-cell-cal { font-size:0.7rem; line-height:1.35; min-width:150px; }
      .hot-ok { color: var(--ia-success-fg, #a7f3d0); font-weight:700; }
      .hot-warn { color: var(--ia-warning-fg, #fbbf24); font-weight:700; }
      .hot-danger { color: var(--ia-danger-fg, #fca5a5); font-weight:700; }
      .hot-sub { display:block; color: var(--ia-fg-soft, #AEAEAE); font-weight:400; }
      .ht-danger { border-color: rgba(239,68,68,0.45); background: rgba(239,68,68,0.10); color: var(--ia-danger-fg, #fca5a5); }
      .ht-grid { display:grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap:6px 14px; margin:6px 0; }
      .ht-grid span { display:block; font-size:0.64rem; letter-spacing:0.04em; text-transform:uppercase; opacity:0.8; }
      .ht-grid strong { font-size:0.82rem; }
      .ht-checked { margin:4px 0 0 0; padding-left:16px; font-size:0.7rem; opacity:0.9; }
      .field-hint-warn { color: var(--ia-warning-fg, #fbbf24); }
      .ctx-load-issues { border:1px solid rgba(239,68,68,0.45); background: rgba(239,68,68,0.10); color: var(--ia-danger-fg, #fca5a5); border-radius:6px; padding:10px 12px; margin-bottom:14px; font-size:0.78rem; }
      .ctx-load-issues strong { display:block; font-size:0.72rem; letter-spacing:0.04em; margin-bottom:6px; }
      .ctx-load-issues ul { margin:0; padding-left:18px; line-height:1.6; }
      .sig-unresolved { color: var(--ia-warning-fg, #fbbf24); font-weight:600; }

      .me-actionbar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
      .btn-action { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; border:1px solid var(--color-input-border, #444); background: var(--color-graphite-800, #222); color: var(--color-text-primary); font-size:0.85rem; font-weight:500; cursor:pointer; }
      .btn-action:hover { filter:brightness(1.15); }
      .btn-action.primary { background: var(--color-gold-main, #c9a04b); border-color: var(--color-gold-main, #c9a04b); color: var(--ia-action-fg, #1a1a1a); font-weight:700; }
      .btn-action.sm { padding:5px 10px; font-size:0.78rem; }
      .btn-action.danger { border-color: var(--status-danger-solid, #ef4444); color: var(--status-danger-solid, #ef4444); }
      .btn-action[disabled] { opacity:0.5; cursor:not-allowed; }
      .btn-action svg, .btn-action i { width:14px; height:14px; }

      .me-notice { padding:10px 14px; border-radius:6px; background: rgba(96,165,250,0.1); border:1px solid rgba(96,165,250,0.35); color: var(--ia-info-fg, #93c5fd); font-size:0.82rem; margin-bottom:14px; }
      .me-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.65); z-index:9500; display:flex; align-items:center; justify-content:center; padding:16px; }
      .me-modal { width:100%; max-width:520px; background:var(--color-surface-primary,#18181b); border:1px solid var(--color-graphite-300,#3f3f46); border-radius:10px; overflow:hidden; }
      .me-modal-head { padding:14px 18px; font-size:0.9rem; font-weight:700; letter-spacing:0.03em; border-bottom:1px solid var(--color-graphite-300,#3f3f46); }
      .me-modal-head.danger { color: var(--ia-danger-fg, #fca5a5); }
      .me-modal-body { padding:16px 18px; font-size:0.84rem; line-height:1.55; display:flex; flex-direction:column; gap:10px; }
      .me-modal-actions { padding:12px 18px; display:flex; justify-content:flex-end; gap:10px; border-top:1px solid var(--color-graphite-300,#3f3f46); }
      .me-pend-list { display:flex; flex-direction:column; gap:12px; }
      .me-pend-group ul { margin:6px 0 0; padding-left:20px; color:var(--color-text-muted,#a1a1aa); }
      .me-pend-item { background:none; border:none; padding:0; font-size:0.8rem; font-weight:700; letter-spacing:0.03em; color: var(--ia-danger-fg, #fca5a5); cursor:pointer; text-align:left; }
      .me-pend-item:hover { text-decoration:underline; }
      .me-pend-hint { color:var(--color-text-muted,#a1a1aa); font-size:0.78rem; }
      .me-notice-ok { background: rgba(74,222,128,0.1); border-color: rgba(74,222,128,0.35); color: var(--ia-success-fg, #86efac); }
      .me-notice-error { background: rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); }
      .me-notice-error ul { margin:0; padding-left:18px; }

      .me-layout { display:grid; grid-template-columns: 160px 1fr; gap:20px; align-items:start; }
      .me-nav { display:flex; flex-direction:column; gap:2px; position:sticky; top:12px; }
      .me-nav-item { display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:6px; color: var(--color-text-disabled); font-size:0.78rem; text-decoration:none; cursor:pointer; }
      .me-nav-item:hover { background: var(--color-graphite-800, rgba(255,255,255,0.05)); color: var(--color-text-primary); }
      .nav-num { font-weight:700; color: var(--color-gold-main, #c9a04b); font-size:0.72rem; }

      .me-main { display:grid; grid-template-columns: 1.7fr 1fr; gap:18px; }
      .me-col { display:flex; flex-direction:column; gap:16px; }

      .me-card { background: var(--color-graphite-800, #18181b); border:1px solid var(--color-graphite-700, #27272a); border-radius:8px; padding:16px; }
      .card-title { font-size:0.85rem; font-weight:700; color: var(--color-text-primary); display:flex; align-items:center; gap:8px; margin-bottom:12px; text-transform:uppercase; letter-spacing:0.02em; }
      .card-title-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
      .card-title-row .card-title { margin-bottom:0; }
      .card-num { display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:4px; background: var(--color-gold-main, #c9a04b); color: var(--ia-action-fg, #1a1a1a); font-size:0.7rem; font-weight:800; }

      .card-grid { display:grid; gap:12px; }
      .card-grid.cols-3 { grid-template-columns: repeat(3, 1fr); }
      .field { display:flex; flex-direction:column; gap:4px; font-size:0.72rem; color: var(--color-text-disabled); }
      .ctl { background: var(--color-input-bg, #1e1e22); border:1px solid var(--color-input-border, #3a3a3f); border-radius:6px; padding:7px 10px; color: var(--color-text-primary); font-size:0.82rem; }
      .ctl:disabled { opacity:0.6; cursor:not-allowed; }
      .ctl.textarea { min-height:110px; resize:vertical; font-family:inherit; }
      .ctl[readonly] { background: rgba(255,255,255,0.04); color: var(--ia-fg-soft, #AEAEAE); cursor: default; }
      .field-hint { font-size:0.68rem; color: var(--ia-fg-soft, #AEAEAE); margin-top:2px; }

      .equip-cols { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
      .equip-box { border:1px solid var(--color-graphite-700, #27272a); border-radius:6px; padding:12px; display:flex; flex-direction:column; gap:10px; }
      .equip-box-title { font-size:0.72rem; font-weight:700; color: var(--color-text-disabled); text-transform:uppercase; }

      .me-table-wrap { overflow-x:auto; }
      .me-table { width:100%; border-collapse:collapse; font-size:0.78rem; }
      .me-table th { text-align:left; padding:8px; background: rgba(255,255,255,0.04); color: var(--color-text-disabled); font-size:0.68rem; text-transform:uppercase; white-space:nowrap; }
      .me-table td { padding:4px; border-top:1px solid var(--color-graphite-700, #27272a); }
      .me-table .pt-id { padding:8px; font-weight:700; color: var(--color-text-primary); }
      .me-table-empty { text-align:center; color: var(--color-text-disabled); padding:20px !important; }
      .ctl-cell { width:100%; background: var(--color-input-bg, #1e1e22); border:1px solid transparent; border-radius:4px; padding:6px 8px; color: var(--color-text-primary); font-size:0.78rem; }
      .ctl-cell:focus { border-color: var(--color-gold-main, #c9a04b); }
      .ctl-cell.num { text-align:center; }
      .laudo-badge { display:inline-block; padding:3px 8px; border-radius:4px; font-size:0.72rem; font-weight:700; white-space:nowrap; }
      .laudo-approved { background: rgba(74,222,128,0.18); color: var(--ia-success-fg, #86efac); }
      .laudo-rejected { background: rgba(239,68,68,0.18); color: var(--ia-danger-fg, #fca5a5); }
      .laudo-adpc { background: rgba(250,204,21,0.18); color: var(--ia-warning-fg, #fde047); }
      .laudo-pending { background: rgba(174,174,174,0.15); color: var(--ia-fg-soft, #AEAEAE); }
      .laudo-select { margin-top:4px; font-size:0.72rem; }
      .btn-icon-danger { background:transparent; border:none; color: var(--status-danger-solid, #ef4444); cursor:pointer; padding:4px; }
      .btn-icon-danger svg, .btn-icon-danger i { width:14px; height:14px; }

      .legend-text { font-size:0.78rem; color: var(--color-text-primary); line-height:1.6; }
      .legend-note { font-size:0.72rem; color: var(--color-text-disabled); margin:8px 0 0 0; }

      .croqui-dropzone { display:flex; flex-direction:column; align-items:center; gap:8px; padding:32px 16px; border:2px dashed var(--color-graphite-700, #3a3a3f); border-radius:8px; cursor:pointer; color: var(--color-text-disabled); text-align:center; }
      .croqui-dropzone:hover { border-color: var(--color-gold-main, #c9a04b); }
      .croqui-dropzone small { font-size:0.68rem; }
      .croqui-preview img { width:100%; border-radius:6px; }
      .croqui-actions { display:flex; gap:8px; margin-top:8px; }

      .sig-row { display:grid; grid-template-columns: 110px 1fr auto; gap:10px; align-items:center; padding:10px 0; border-top:1px solid var(--color-graphite-700, #27272a); font-size:0.78rem; }
      .sig-row:first-of-type { border-top:none; }
      .sig-lbl { color: var(--color-text-disabled); font-weight:600; }
      .sig-val { color: var(--color-text-primary); }
      .sig-val small { color: var(--color-text-disabled); }
      .sig-status { font-size:0.68rem; color: var(--color-text-disabled); white-space:nowrap; }

      .pdf-modal-container { position:fixed; inset:0; background:rgba(0,0,0,0.85); align-items:center; justify-content:center; z-index:10000; padding:20px; }
      .pdf-modal-content { background: var(--color-graphite-800, #18181b); border:1px solid var(--color-graphite-700, #27272a); border-radius:12px; width:100%; max-width:1100px; height:92vh; display:flex; flex-direction:column; overflow:hidden; }
      .pdf-modal-header { padding:12px 18px; background: var(--color-primary); border-bottom:1px solid var(--color-graphite-700, #27272a); display:flex; justify-content:space-between; align-items:center; color: var(--color-text-primary); }

      @media (max-width: 900px) {
        .me-layout { grid-template-columns: 1fr; }
        .me-nav { position:static; flex-direction:row; flex-wrap:wrap; }
        .me-main { grid-template-columns: 1fr; }
        .card-grid.cols-3 { grid-template-columns: 1fr 1fr; }
        .equip-cols { grid-template-columns: 1fr; }
      }
      @media (max-width: 560px) {
        .card-grid.cols-3 { grid-template-columns: 1fr; }
      }
    `;
  }
}

if (!customElements.get("app-me-report-page")) {
  customElements.define("app-me-report-page", AppMeReportPage);
}
